import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import localforage from 'localforage';
import { format } from 'date-fns';
import { 
  Search, PlusCircle, X, ChevronDown, ChevronUp, Edit, Trash2, Save, 
  Check, AlertTriangle, Users, UserPlus, UserMinus, Settings, Menu, 
  FileText, ArrowRight, Zap, Eye, EyeOff, Info, MoreHorizontal, 
  Filter, DownloadCloud, Upload, RefreshCw, AlignLeft, GitBranch,
  Shield, Sliders, Lock, Unlock, Copy, BarChart2, Clock, ChevronLeft, ChevronRight
} from 'lucide-react';

// Base URL for API
const BASE_URL = 'https://lager-app-api.sunrise-avengers.ch:443';

const GroupManagementScreen = ({ onClose, accentColor = '#e6376e' }) => {
  // ========== STATE MANAGEMENT ==========
  const [groups, setGroups] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [view, setView] = useState('list'); // 'list', 'grid', 'table'
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [filterOptions, setFilterOptions] = useState({
    memberCount: 'all', // 'all', 'small', 'medium', 'large'
    status: 'all', // 'all', 'active', 'inactive'
  });
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newGroup, setNewGroup] = useState({
    name: '',
    description: '',
    colorCode: accentColor,
    membersLimit: 10,
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [draggedMember, setDraggedMember] = useState(null);
  const [dragOverGroup, setDragOverGroup] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedTab, setSelectedTab] = useState('info'); // 'info', 'members', 'settings', 'analytics'
  const [activityLog, setActivityLog] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);
  const [statusFilter, setStatusFilter] = useState('all');
  const [hoverGroup, setHoverGroup] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [addMemberMode, setAddMemberMode] = useState(false);
  const [groupAnalytics, setGroupAnalytics] = useState({});
  const [isAnalyticsLoading, setIsAnalyticsLoading] = useState(false);
  const [isActionMenuOpen, setIsActionMenuOpen] = useState({});
  const [groupToRename, setGroupToRename] = useState(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [tableColumns, setTableColumns] = useState({
    name: true,
    members: true,
    created: true,
    status: true,
    actions: true,
  });
  const [isColumnsMenuOpen, setIsColumnsMenuOpen] = useState(false);
  const [isAdvancedSettingsOpen, setIsAdvancedSettingsOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [memberSearchValue, setMemberSearchValue] = useState('');
  const [isMemberLoading, setIsMemberLoading] = useState(false);
  const [activeAnimation, setActiveAnimation] = useState(null);
  const [viewTransition, setViewTransition] = useState(false);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [stats, setStats] = useState({
    totalGroups: 0,
    totalMembers: 0,
    avgMembersPerGroup: 0,
    unassignedUsers: 0
  });

  // Performance optimization with scroll virtualization
  const scrollRef = useRef(null);
  const observers = useRef({});
  const inViewGroups = useRef(new Set());
  
  // Animation references
  const animationRef = useRef(null);
  const loadingAnimationRef = useRef(null);
  const refreshTimeoutRef = useRef(null);

  // ========== CALCULATED VALUES ==========
  // Total pages calculation based on filtered groups and items per page
  const totalPages = Math.ceil(filteredGroups.length / itemsPerPage);
  
  // Get current groups for pagination
  const currentGroups = useMemo(() => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return filteredGroups.slice(indexOfFirstItem, indexOfLastItem);
  }, [filteredGroups, currentPage, itemsPerPage]);

  // Memo for member filtering based on search
  const filteredAvailableUsers = useMemo(() => {
    return availableUsers.filter((user) => {
      if (!memberSearchValue) return true;
      return (
        user.username.toLowerCase().includes(memberSearchValue.toLowerCase()) ||
        user.email.toLowerCase().includes(memberSearchValue.toLowerCase())
      );
    });
  }, [availableUsers, memberSearchValue]);

  // Calculate filtered members for selected group
  const filteredGroupMembers = useMemo(() => {
    if (!selectedGroup) return [];
    return selectedGroup.members.filter((member) => {
      if (!memberSearchValue) return true;
      return (
        member.username.toLowerCase().includes(memberSearchValue.toLowerCase()) ||
        member.email.toLowerCase().includes(memberSearchValue.toLowerCase())
      );
    });
  }, [selectedGroup, memberSearchValue]);

  // ========== API CALLS & DATA HANDLING ==========
  const fetchGroups = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const token = await localforage.getItem('token');
      
      const response = await fetch(`${BASE_URL}/api/groups`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch groups');
      }
      
      const data = await response.json();
      
      // Process API response and validate dates
      const fetchedGroups = (data.groups || []).map(group => ({
        ...group,
        // Ensure createdAt is valid or set to current date
        createdAt: group.createdAt || new Date().toISOString()
      }));
  
      // Calculate statistics
      const totalGroups = fetchedGroups.length;
      const totalMembers = fetchedGroups.reduce((sum, group) => sum + (group.memberCount || 0), 0);
      const avgMembersPerGroup = totalGroups > 0 ? totalMembers / totalGroups : 0;
      
      setStats({
        totalGroups,
        totalMembers,
        avgMembersPerGroup,
        unassignedUsers: availableUsers.length
      });
      
      setGroups(fetchedGroups);
      setFilteredGroups(fetchedGroups);
      startLoadingAnimation();
      
    } catch (err) {
      console.error('Error fetching groups:', err);
      setError('Failed to load groups. Please try again.');
    } finally {
      // Simulate more realistic loading for demo purposes
      setTimeout(() => {
        setIsLoading(false);
      }, 800);
    }
  };

  const fetchGroupDetails = async (groupId) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const token = await localforage.getItem('token');
      
      const response = await fetch(`${BASE_URL}/api/groups/${groupId}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch group details');
      }
      
      const data = await response.json();
      setSelectedGroup(data);
      
      // Also fetch the activity log for this group
      fetchActivityLog(groupId);
      
      // Generate analytics for this group
      generateGroupAnalytics(groupId);
      
    } catch (err) {
      console.error('Error fetching group details:', err);
      setError('Failed to load group details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUnassignedUsers = async () => {
    try {
      const token = await localforage.getItem('token');
      
      const response = await fetch(`${BASE_URL}/api/groups/unassigned-users`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch unassigned users');
      }
      
      const data = await response.json();
      setAvailableUsers(data.users || []);
      
      // Update stats
      setStats(prevStats => ({
        ...prevStats,
        unassignedUsers: data.users?.length || 0
      }));
      
    } catch (err) {
      console.error('Error fetching unassigned users:', err);
      setError('Failed to load unassigned users. Please try again.');
    }
  };

  const fetchActivityLog = async (groupId) => {
    try {
      const token = await localforage.getItem('token');
      
      const response = await fetch(`${BASE_URL}/api/groups/${groupId}/activity`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch activity log');
      }
      
      const data = await response.json();
      setActivityLog(data.activities || []);
      
    } catch (err) {
      console.error('Error fetching activity log:', err);
      // Don't set error, it's not critical
    }
  };

  const generateGroupAnalytics = async (groupId) => {
    setIsAnalyticsLoading(true);
    
    try {
      const token = await localforage.getItem('token');
      
      const response = await fetch(`${BASE_URL}/api/groups/${groupId}/analytics`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch analytics');
      }
      
      const data = await response.json();
      setGroupAnalytics(data);
      
    } catch (err) {
      console.error('Error fetching analytics:', err);
      // If analytics endpoint doesn't exist, generate mock data
      if (selectedGroup) {
        const mockData = {
          totalMembers: selectedGroup.members.length,
          activitiesThisMonth: Math.floor(Math.random() * 50) + 10,
          memberGrowth: Math.floor(Math.random() * 30) - 10,
          completionRate: Math.floor(Math.random() * 40) + 60,
          activityByDay: [
            { day: 'Mon', count: Math.floor(Math.random() * 10) + 5 },
            { day: 'Tue', count: Math.floor(Math.random() * 10) + 5 },
            { day: 'Wed', count: Math.floor(Math.random() * 10) + 5 },
            { day: 'Thu', count: Math.floor(Math.random() * 10) + 5 },
            { day: 'Fri', count: Math.floor(Math.random() * 10) + 5 },
            { day: 'Sat', count: Math.floor(Math.random() * 5) },
            { day: 'Sun', count: Math.floor(Math.random() * 3) }
          ],
          topContributors: selectedGroup.members
            .slice(0, Math.min(3, selectedGroup.members.length))
            .map(member => ({
              ...member,
              contributions: Math.floor(Math.random() * 20) + 5
            }))
        };
        setGroupAnalytics(mockData);
      }
    } finally {
      setIsAnalyticsLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const token = await localforage.getItem('token');
      
      const response = await fetch(`${BASE_URL}/api/notifications`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }
      
      const data = await response.json();
      setNotifications(data.notifications || []);
      
    } catch (err) {
      console.error('Error fetching notifications:', err);
      // If notifications endpoint doesn't exist, generate mock data
      const mockNotifications = [
        {
          id: 1,
          message: "New member added to Sunrise Team Alpha",
          timestamp: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
          read: false,
          type: "member"
        },
        {
          id: 2,
          message: "Group 'Beta Testers' settings updated",
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 3).toISOString(),
          read: false,
          type: "settings"
        },
        {
          id: 3,
          message: "UX/UI Design group reached member limit",
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
          read: true,
          type: "alert"
        }
      ];
      setNotifications(mockNotifications);
    }
  };

  // ========== CRUD OPERATIONS ==========
  const handleCreateGroup = async () => {
    setIsLoading(true);
    
    try {
      const token = await localforage.getItem('token');
      
      // Make sure the group has a valid date
      const groupToCreate = {
        ...newGroup,
        createdAt: new Date().toISOString() // Add current date as fallback
      };
      
      const response = await fetch(`${BASE_URL}/api/groups`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(groupToCreate)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create group');
      }
      
      // Reset form and close modal
      setNewGroup({
        name: '',
        description: '',
        colorCode: accentColor,
        membersLimit: 10
      });
      setIsCreateModalOpen(false);
      
      // Refresh groups list
      fetchGroups();
      
      // Show success message
      animateSuccess('Group created successfully!');
      
    } catch (err) {
      console.error('Error creating group:', err);
      setError(err.message || 'Failed to create group');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateGroup = async () => {
    if (!selectedGroup) return;
    
    setIsLoading(true);
    
    try {
      const token = await localforage.getItem('token');
      
      const response = await fetch(`${BASE_URL}/api/groups/${selectedGroup.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: selectedGroup.name,
          description: selectedGroup.description,
          colorCode: selectedGroup.colorCode,
          status: selectedGroup.status,
          membersLimit: selectedGroup.membersLimit
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update group');
      }
      
      // Close edit mode
      setIsEditMode(false);
      
      // Refresh groups list
      fetchGroups();
      
      // Show success message
      animateSuccess('Group updated successfully!');
      
    } catch (err) {
      console.error('Error updating group:', err);
      setError(err.message || 'Failed to update group');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!selectedGroup) return;
    
    setIsLoading(true);
    
    try {
      const token = await localforage.getItem('token');
      
      const response = await fetch(`${BASE_URL}/api/groups/${selectedGroup.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete group');
      }
      
      // Reset selected group and close modal
      setSelectedGroup(null);
      setIsDeleteModalOpen(false);
      
      // Refresh groups list
      fetchGroups();
      
      // Show success message
      animateSuccess('Group deleted successfully!');
      
    } catch (err) {
      console.error('Error deleting group:', err);
      setError(err.message || 'Failed to delete group');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddMember = async (userId) => {
    if (!selectedGroup) return;
    
    setIsMemberLoading(true);
    
    try {
      const token = await localforage.getItem('token');
      
      // Find user in available users
      const userToAdd = availableUsers.find(user => user.id === userId);
      
      if (!userToAdd) {
        setError('User not found');
        setIsMemberLoading(false);
        return;
      }
      
      // Check if group has reached member limit
      if (selectedGroup.members.length >= selectedGroup.membersLimit) {
        setError('Group has reached member limit');
        setIsMemberLoading(false);
        return;
      }
      
      const response = await fetch(`${BASE_URL}/api/groups/${selectedGroup.id}/members`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: userToAdd.email })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add member');
      }
      
      // Refresh group details
      fetchGroupDetails(selectedGroup.id);
      
      // Refresh unassigned users
      fetchUnassignedUsers();
      
      // Show success message
      animateSuccess('Member added successfully!');
      
    } catch (err) {
      console.error('Error adding member:', err);
      setError(err.message || 'Failed to add member');
    } finally {
      setIsMemberLoading(false);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!selectedGroup) return;
    
    setIsMemberLoading(true);
    
    try {
      const token = await localforage.getItem('token');
      
      const response = await fetch(`${BASE_URL}/api/groups/${selectedGroup.id}/members/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to remove member');
      }
      
      // Refresh group details
      fetchGroupDetails(selectedGroup.id);
      
      // Refresh unassigned users
      fetchUnassignedUsers();
      
      // Show success message
      animateSuccess('Member removed successfully!');
      
    } catch (err) {
      console.error('Error removing member:', err);
      setError(err.message || 'Failed to remove member');
    } finally {
      setIsMemberLoading(false);
    }
  };

  const handleRenameGroup = async () => {
    if (!newGroupName.trim() || !groupToRename) {
      setError('Group name cannot be empty');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const token = await localforage.getItem('token');
      
      // Find the group
      const groupToUpdate = groups.find(group => group.id === groupToRename);
      
      if (!groupToUpdate) {
        setError('Group not found');
        setIsLoading(false);
        return;
      }
      
      const response = await fetch(`${BASE_URL}/api/groups/${groupToRename}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...groupToUpdate,
          name: newGroupName
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to rename group');
      }
      
      // Refresh groups list
      fetchGroups();
      
      // If this is the selected group, update that too
      if (selectedGroup && selectedGroup.id === groupToRename) {
        fetchGroupDetails(groupToRename);
      }
      
      // Reset state
      setGroupToRename(null);
      setNewGroupName('');
      
      // Show success message
      animateSuccess('Group renamed successfully!');
      
    } catch (err) {
      console.error('Error renaming group:', err);
      setError(err.message || 'Failed to rename group');
    } finally {
      setIsLoading(false);
    }
  };

  // ========== ANIMATIONS & UI INTERACTIONS ==========
  const startLoadingAnimation = () => {
    let progress = 0;
    const duration = 2000; // 2 seconds
    const step = 16; // 60fps approx
    
    loadingAnimationRef.current = setInterval(() => {
      progress += (step / duration) * 100;
      
      // Update loading indicator (could dispatch to state or update DOM directly)
      const loadingBar = document.querySelector('.loading-bar-fill');
      if (loadingBar) {
        loadingBar.style.width = `${Math.min(progress, 100)}%`;
      }
      
      if (progress >= 100) {
        clearInterval(loadingAnimationRef.current);
      }
    }, step);
  };
  
  const animateSuccess = (message) => {
    setSuccess(message);
    setTimeout(() => {
      setSuccess(null);
    }, 3000);
  };

  const triggerViewTransition = (newView) => {
    setViewTransition(true);
    setTimeout(() => {
      setView(newView);
      setViewTransition(false);
    }, 300);
  };

  const pulseAnimation = (elementId) => {
    setActiveAnimation(elementId);
    setTimeout(() => {
      setActiveAnimation(null);
    }, 700);
  };

  // ========== EVENT HANDLERS ==========
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleFilterChange = (filterKey, value) => {
    setFilterOptions(prevFilters => ({
      ...prevFilters,
      [filterKey]: value
    }));
  };

  const clearFilters = () => {
    setFilterOptions({
      memberCount: 'all',
      status: 'all'
    });
    setSearchValue('');
  };

  const handleGroupSelect = (group) => {
    fetchGroupDetails(group.id);
    setSelectedTab('info');
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
      // Scroll to top of list
      if (scrollRef.current) {
        scrollRef.current.scrollTop = 0;
      }
    }
  };

  const toggleExpandGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const handleActionMenu = (groupId) => {
    setIsActionMenuOpen(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const handleDragStart = (e, member) => {
    setDraggedMember(member);
  };

  const handleDragOver = (e, groupId) => {
    e.preventDefault();
    setDragOverGroup(groupId);
  };

  const handleDrop = async (e, targetGroupId) => {
    e.preventDefault();
    
    if (!draggedMember || !targetGroupId) return;
    
    try {
      const token = await localforage.getItem('token');
      
      // Find source group
      const sourceGroup = groups.find(group => 
        group.members && group.members.some(member => member.id === draggedMember.id)
      );
      
      if (!sourceGroup) {
        setError('Source group not found');
        return;
      }
      
      // Remove from source group
      const removeResponse = await fetch(`${BASE_URL}/api/groups/${sourceGroup.id}/members/${draggedMember.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!removeResponse.ok) {
        const errorData = await removeResponse.json();
        throw new Error(errorData.message || 'Failed to remove member from source group');
      }
      
      // Add to target group
      const addResponse = await fetch(`${BASE_URL}/api/groups/${targetGroupId}/members`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: draggedMember.email })
      });
      
      if (!addResponse.ok) {
        const errorData = await addResponse.json();
        throw new Error(errorData.message || 'Failed to add member to target group');
      }
      
      // Refresh groups list
      fetchGroups();
      
      // If selected group is affected, refresh it
      if (selectedGroup && (selectedGroup.id === sourceGroup.id || selectedGroup.id === targetGroupId)) {
        fetchGroupDetails(selectedGroup.id);
      }
      
      // Show success message
      animateSuccess(`Member ${draggedMember.username} moved successfully`);
      
    } catch (err) {
      console.error('Error moving member:', err);
      setError(err.message || 'Failed to move member');
    } finally {
      // Reset drag state
      setDraggedMember(null);
      setDragOverGroup(null);
    }
  };

  const handleDragEnd = () => {
    setDraggedMember(null);
    setDragOverGroup(null);
  };

  const handleErrorClose = () => {
    setError(null);
  };

  const handleSuccessClose = () => {
    setSuccess(null);
  };

  const refreshData = () => {
    setIsLoading(true);
    fetchGroups();
    fetchUnassignedUsers();
    fetchNotifications();
    
    // Show loading for at least 1 second for better UX
    refreshTimeoutRef.current = setTimeout(() => {
      animateSuccess('Data refreshed successfully!');
    }, 1000);
  };

  // Format date for display
  const formatDate = (dateString) => {
    try {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      // Check if the date is valid
      if (isNaN(date.getTime())) return 'Invalid date';
      return format(date, 'MMM d, yyyy');
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };
  
  // Format time for display (relative time) - with error handling
  const formatTime = (dateString) => {
    try {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      // Check if the date is valid
      if (isNaN(date.getTime())) return 'Invalid date';
      
      const now = new Date();
      const diffInMinutes = Math.round((now - date) / (1000 * 60));
      
      if (diffInMinutes < 60) {
        return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
      } else if (diffInMinutes < 1440) {
        const hours = Math.floor(diffInMinutes / 60);
        return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
      } else {
        const days = Math.floor(diffInMinutes / 1440);
        return `${days} day${days !== 1 ? 's' : ''} ago`;
      }
    } catch (error) {
      console.error('Error formatting time:', error);
      return 'Invalid date';
    }
  };
  

  // ========== EFFECTS ==========
  // Apply filters and sorting
  useEffect(() => {
    let result = [...groups];
    
    // Apply search filter
    if (searchValue) {
      result = result.filter((group) => {
        return (
          group.name.toLowerCase().includes(searchValue.toLowerCase()) ||
          (group.description && group.description.toLowerCase().includes(searchValue.toLowerCase()))
        );
      });
    }
    
    // Apply member count filter
    if (filterOptions.memberCount !== 'all') {
      switch(filterOptions.memberCount) {
        case 'small':
          result = result.filter(group => (group.memberCount || 0) < 5);
          break;
        case 'medium':
          result = result.filter(group => (group.memberCount || 0) >= 5 && (group.memberCount || 0) < 10);
          break;
        case 'large':
          result = result.filter(group => (group.memberCount || 0) >= 10);
          break;
        default:
          break;
      }
    }
    
    // Apply status filter
    if (filterOptions.status !== 'all') {
      result = result.filter(group => group.status === filterOptions.status);
    }
    
    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        if (sortConfig.key === 'members') {
          return sortConfig.direction === 'asc' 
            ? (a.memberCount || 0) - (b.memberCount || 0)
            : (b.memberCount || 0) - (a.memberCount || 0);
        } else if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        } else if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    setFilteredGroups(result);
    setCurrentPage(1); // Reset to first page when filters change
  }, [groups, searchValue, filterOptions, sortConfig]);

  // Initial data fetch
  useEffect(() => {
    fetchGroups();
    fetchUnassignedUsers();
    fetchNotifications();
    
    // Start loading animation
    startLoadingAnimation();
    
    // Cleanup animation on unmount
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (loadingAnimationRef.current) {
        clearInterval(loadingAnimationRef.current);
      }
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  // ========== RENDER HELPERS ==========
  const renderStatusBadge = (status) => {
    const statusColors = {
      active: {
        bg: '#10b981',
        text: '#ffffff'
      },
      inactive: {
        bg: '#6b7280',
        text: '#ffffff'
      }
    };
    
    const style = {
      backgroundColor: statusColors[status]?.bg || '#6b7280',
      color: statusColors[status]?.text || '#ffffff',
      padding: '0.25rem 0.75rem',
      borderRadius: '9999px',
      fontSize: '0.75rem',
      fontWeight: '600',
      textTransform: 'capitalize',
      display: 'inline-block'
    };
    
    return (
      <span style={style}>
        {status}
      </span>
    );
  };

  const renderMemberAvatar = (member, size = 38) => {
    return (
      <div className="member-avatar" style={{
        width: size,
        height: size,
        borderRadius: '50%',
        overflow: 'hidden',
        position: 'relative'
      }}>
        <img 
          src={member.profileImagePath ? `${BASE_URL}${member.profileImagePath}` : `/api/placeholder/${size}/${size}`} 
          alt={member.username}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />
      </div>
    );
  };

  const renderGroupCard = (group) => {
    const isExpanded = expandedGroups[group.id];
    const isDragOver = dragOverGroup === group.id;
    const isHovered = hoverGroup === group.id;
    const isActionOpen = isActionMenuOpen[group.id];
    
    return (
      <motion.div 
        className={`group-card ${isDragOver ? 'drag-over' : ''} ${isHovered ? 'hovered' : ''}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        key={group.id}
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: isDragOver 
            ? `0 0 0 2px ${group.colorCode || accentColor}, 0 4px 20px rgba(0, 0, 0, 0.1)` 
            : isHovered 
              ? '0 10px 25px rgba(0, 0, 0, 0.1)' 
              : '0 2px 10px rgba(0, 0, 0, 0.05)',
          transition: 'all 0.3s ease',
          marginBottom: '1rem',
          border: isExpanded ? '1px solid #e5e7eb' : '1px solid transparent',
          transform: isDragOver ? 'scale(1.02)' : 'scale(1)',
        }}
        onMouseEnter={() => setHoverGroup(group.id)}
        onMouseLeave={() => setHoverGroup(null)}
        onDragOver={(e) => handleDragOver(e, group.id)}
        onDrop={(e) => handleDrop(e, group.id)}
      >
        <div 
          className="group-card-header"
          style={{
            padding: '1rem',
            borderBottom: isExpanded ? '1px solid #e5e7eb' : 'none',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'relative',
            cursor: 'pointer',
            backgroundColor: isExpanded ? '#f9fafb' : '#ffffff',
          }}
          onClick={() => toggleExpandGroup(group.id)}
        >
          <div className="group-card-title" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div 
              className="group-color-indicator" 
              style={{
                width: '1rem',
                height: '1rem',
                borderRadius: '0.25rem',
                backgroundColor: group.colorCode || accentColor,
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}
            />
            <h3 
              style={{ 
                margin: 0, 
                fontSize: '1rem', 
                fontWeight: '600', 
                color: '#111827',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              {group.name}
              {group.status === 'inactive' && (
                <span style={{ 
                  fontSize: '0.75rem', 
                  fontWeight: 'normal', 
                  padding: '0.125rem 0.375rem', 
                  borderRadius: '9999px', 
                  backgroundColor: '#e5e7eb',
                  color: '#6b7280'
                }}>
                  Inactive
                </span>
              )}
            </h3>
          </div>
          <div className="group-card-actions" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div className="group-members-preview" style={{ display: 'flex', alignItems: 'center' }}>
              <div className="member-count" style={{ 
                padding: '0.25rem 0.5rem', 
                borderRadius: '0.375rem', 
                backgroundColor: '#f3f4f6',
                color: '#6b7280',
                fontSize: '0.75rem',
                fontWeight: '600', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.25rem' 
              }}>
                <Users size={14} />
                {group.memberCount || 0}
              </div>
            </div>
            <div className="group-card-toggle" style={{ position: 'relative' }}>
              <button 
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '0.25rem',
                  color: '#6b7280',
                  outline: 'none',
                  transition: 'color 0.2s ease',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleActionMenu(group.id);
                }}
              >
                <MoreHorizontal size={18} />
              </button>
              
              {isActionOpen && (
                <div 
                  className="action-menu" 
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: '100%',
                    marginTop: '0.25rem',
                    backgroundColor: '#ffffff',
                    borderRadius: '0.375rem',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    border: '1px solid #e5e7eb',
                    zIndex: 10,
                    width: '12rem',
                    overflow: 'hidden'
                  }}
                >
                  <ul style={{ margin: 0, padding: '0.25rem 0', listStyle: 'none' }}>
                    <li>
                      <button 
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.5rem 1rem',
                          width: '100%',
                          textAlign: 'left',
                          backgroundColor: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#111827',
                          fontSize: '0.875rem'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleGroupSelect(group);
                          setIsActionMenuOpen({});
                        }}
                      >
                        <Eye size={16} />
                        View Details
                      </button>
                    </li>
                    <li>
                      <button 
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.5rem 1rem',
                          width: '100%',
                          textAlign: 'left',
                          backgroundColor: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#111827',
                          fontSize: '0.875rem'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setGroupToRename(group.id);
                          setNewGroupName(group.name);
                          setIsActionMenuOpen({});
                        }}
                      >
                        <Edit size={16} />
                        Rename
                      </button>
                    </li>
                    <li>
                      <button 
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.5rem 1rem',
                          width: '100%',
                          textAlign: 'left',
                          backgroundColor: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          color: group.status === 'active' ? '#6b7280' : '#10b981',
                          fontSize: '0.875rem'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          // In a real implementation, this would update the group status via API
                          const updatedGroups = groups.map(g => {
                            if (g.id === group.id) {
                              return {
                                ...g,
                                status: g.status === 'active' ? 'inactive' : 'active'
                              };
                            }
                            return g;
                          });
                          setGroups(updatedGroups);
                          setIsActionMenuOpen({});
                        }}
                      >
                        {group.status === 'active' ? <EyeOff size={16} /> : <Eye size={16} />}
                        {group.status === 'active' ? 'Deactivate' : 'Activate'}
                      </button>
                    </li>
                    <li>
                      <button 
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.5rem 1rem',
                          width: '100%',
                          textAlign: 'left',
                          backgroundColor: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#ef4444',
                          fontSize: '0.875rem'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedGroup(group);
                          setIsDeleteModalOpen(true);
                          setIsActionMenuOpen({});
                        }}
                      >
                        <Trash2 size={16} />
                        Delete
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
            <motion.div 
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <ChevronDown size={18} color="#6b7280" />
            </motion.div>
          </div>
        </div>
        
        {/* Group Card Content (expanded) */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div 
              className="group-card-content"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              style={{ padding: '1rem', overflow: 'hidden' }}
            >
              <div className="group-info" style={{ marginBottom: '1rem' }}>
                <p style={{ 
                  margin: '0 0 0.75rem 0', 
                  fontSize: '0.875rem', 
                  color: '#4b5563',
                  lineHeight: '1.5'
                }}>
                  {group.description || 'No description available.'}
                </p>
                <div className="group-meta" style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap',
                  gap: '1rem', 
                  fontSize: '0.75rem', 
                  color: '#6b7280' 
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <Clock size={14} />
                    Created {formatDate(group.createdAt)}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <Users size={14} />
                    {group.memberCount || 0} members
                  </div>
                </div>
              </div>
              
              <div className="group-actions" style={{ 
                display: 'flex', 
                justifyContent: 'flex-end',
                gap: '0.5rem',
                paddingTop: '0.5rem',
                borderTop: '1px solid #f3f4f6'
              }}>
                <button 
                  className="group-action-button secondary"
                  style={{
                    backgroundColor: '#f3f4f6',
                    color: '#4b5563',
                    border: 'none',
                    borderRadius: '0.375rem',
                    padding: '0.5rem 0.75rem',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    transition: 'all 0.2s ease',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleGroupSelect(group);
                  }}
                >
                  <Eye size={14} />
                  View Details
                </button>
                <button 
                  className="group-action-button primary"
                  style={{
                    backgroundColor: group.colorCode || accentColor,
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '0.375rem',
                    padding: '0.5rem 0.75rem',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    transition: 'all 0.2s ease',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleGroupSelect(group);
                    setSelectedTab('members');
                  }}
                >
                  <Users size={14} />
                  Manage Members
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  const renderTableView = () => {
    return (
      <div className="table-container" style={{
        borderRadius: '0.5rem',
        overflow: 'hidden',
        border: '1px solid #e5e7eb',
        backgroundColor: '#ffffff',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
      }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '0.875rem'
        }}>
          <thead style={{
            backgroundColor: '#f9fafb',
            color: '#4b5563',
            textAlign: 'left',
            fontWeight: '600',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <tr>
              {tableColumns.name && (
                <th style={{
                  padding: '0.75rem 1rem',
                  borderRight: '1px solid #e5e7eb',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer'
                }} onClick={() => handleSort('name')}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    Group Name
                    {sortConfig.key === 'name' && (
                      <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
              )}
              {tableColumns.members && (
                <th style={{
                  padding: '0.75rem 1rem',
                  borderRight: '1px solid #e5e7eb',
                  whiteSpace: 'nowrap',
                  textAlign: 'center',
                  cursor: 'pointer'
                }} onClick={() => handleSort('members')}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', justifyContent: 'center' }}>
                    <Users size={14} />
                    Members
                    {sortConfig.key === 'members' && (
                      <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
              )}
              {tableColumns.created && (
                <th style={{
                  padding: '0.75rem 1rem',
                  borderRight: '1px solid #e5e7eb',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer'
                }} onClick={() => handleSort('createdAt')}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    Created
                    {sortConfig.key === 'createdAt' && (
                      <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
              )}
              {tableColumns.status && (
                <th style={{
                  padding: '0.75rem 1rem',
                  borderRight: '1px solid #e5e7eb',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer'
                }} onClick={() => handleSort('status')}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    Status
                    {sortConfig.key === 'status' && (
                      <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
              )}
              {tableColumns.actions && (
                <th style={{
                  padding: '0.75rem 1rem',
                  position: 'relative',
                  textAlign: 'center'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', position: 'relative' }}>
                    <button
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0.25rem',
                        borderRadius: '0.25rem',
                        color: '#6b7280'
                      }}
                      onClick={() => setIsColumnsMenuOpen(!isColumnsMenuOpen)}
                    >
                      <Sliders size={14} />
                    </button>
                    
                    {isColumnsMenuOpen && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          right: 0,
                          backgroundColor: '#ffffff',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                          borderRadius: '0.375rem',
                          padding: '0.5rem',
                          zIndex: 50,
                          border: '1px solid #e5e7eb',
                          width: '12rem',
                          marginTop: '0.25rem'
                        }}
                      >
                        <div style={{ 
                          padding: '0.5rem', 
                          borderBottom: '1px solid #e5e7eb',
                          marginBottom: '0.5rem',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          color: '#6b7280'
                        }}>
                          Show/Hide Columns
                        </div>
                        {Object.keys(tableColumns).map(column => (
                          <div 
                            key={column}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '0.375rem 0.5rem',
                              cursor: 'pointer',
                              borderRadius: '0.25rem',
                              transition: 'background-color 0.2s ease',
                              backgroundColor: tableColumns[column] ? `rgba(${parseInt(accentColor.slice(1, 3), 16)}, ${parseInt(accentColor.slice(3, 5), 16)}, ${parseInt(accentColor.slice(5, 7), 16)}, 0.1)` : 'transparent',
                              marginBottom: '0.25rem'
                            }}
                            onClick={() => {
                              if (column === 'name' && tableColumns.name) return; // Don't allow hiding name column
                              if (column === 'actions' && tableColumns.actions) return; // Don't allow hiding actions column
                              
                              setTableColumns(prev => ({
                                ...prev,
                                [column]: !prev[column]
                              }));
                            }}
                          >
                            <span style={{ 
                              textTransform: 'capitalize',
                              fontSize: '0.75rem',
                              color: tableColumns[column] ? '#111827' : '#6b7280'
                            }}>
                              {column}
                            </span>
                            <div style={{
                              width: '1rem',
                              height: '1rem',
                              borderRadius: '0.25rem',
                              border: '1px solid #e5e7eb',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: tableColumns[column] ? accentColor : '#ffffff',
                              color: '#ffffff',
                              fontSize: '0.625rem'
                            }}>
                              {tableColumns[column] && <Check size={10} />}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {currentGroups.map((group, index) => (
              <tr 
                key={group.id}
                style={{ 
                  backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                  borderBottom: '1px solid #e5e7eb',
                  transition: 'background-color 0.2s ease',
                  cursor: 'pointer'
                }}
                onClick={() => handleGroupSelect(group)}
                onMouseEnter={() => setHoverGroup(group.id)}
                onMouseLeave={() => setHoverGroup(null)}
              >
                {tableColumns.name && (
                  <td style={{ 
                    padding: '0.75rem 1rem',
                    borderRight: '1px solid #e5e7eb',
                    color: '#111827',
                    fontWeight: '500',
                    whiteSpace: 'nowrap',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div 
                        style={{ 
                          width: '0.75rem', 
                          height: '0.75rem', 
                          borderRadius: '0.25rem',
                          backgroundColor: group.colorCode || accentColor
                        }}
                      />
                      {groupToRename === group.id ? (
                        <form
                          onSubmit={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleRenameGroup();
                          }}
                          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="text"
                            value={newGroupName}
                            onChange={(e) => setNewGroupName(e.target.value)}
                            autoFocus
                            style={{
                              padding: '0.375rem 0.5rem',
                              borderRadius: '0.25rem',
                              border: '1px solid #e5e7eb',
                              fontSize: '0.875rem',
                              outline: 'none',
                              width: '10rem'
                            }}
                          />
                          <button
                            type="submit"
                            style={{
                              padding: '0.375rem 0.5rem',
                              borderRadius: '0.25rem',
                              border: 'none',
                              backgroundColor: accentColor,
                              color: '#ffffff',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem'
                            }}
                          >
                            <Save size={14} />
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setGroupToRename(null);
                              setNewGroupName('');
                            }}
                            style={{
                              padding: '0.375rem',
                              borderRadius: '0.25rem',
                              border: 'none',
                              backgroundColor: '#f3f4f6',
                              color: '#6b7280',
                              fontSize: '0.75rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <X size={14} />
                          </button>
                        </form>
                      ) : (
                        <span>{group.name}</span>
                      )}
                    </div>
                  </td>
                )}
                {tableColumns.members && (
                  <td style={{ 
                    padding: '0.75rem 1rem',
                    borderRight: '1px solid #e5e7eb',
                    textAlign: 'center',
                    color: '#111827',
                    fontSize: '0.75rem',
                    fontWeight: '600'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      gap: '0.25rem'
                    }}>
                      <Users size={14} color="#6b7280" />
                      <span>{group.memberCount || 0}</span>
                    </div>
                  </td>
                )}
                {tableColumns.created && (
                  <td style={{ 
                    padding: '0.75rem 1rem',
                    borderRight: '1px solid #e5e7eb',
                    color: '#6b7280',
                    fontSize: '0.75rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <Clock size={14} />
                      {formatDate(group.createdAt)}
                    </div>
                  </td>
                )}
                {tableColumns.status && (
                  <td style={{ 
                    padding: '0.75rem 1rem',
                    borderRight: '1px solid #e5e7eb'
                  }}>
                    {renderStatusBadge(group.status || 'active')}
                  </td>
                )}
                {tableColumns.actions && (
                  <td style={{ 
                    padding: '0.5rem',
                    textAlign: 'center'
                  }}>
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}>
                      <button
                        style={{
                          padding: '0.375rem',
                          borderRadius: '0.25rem',
                          border: 'none',
                          backgroundColor: hoverGroup === group.id ? '#f3f4f6' : 'transparent',
                          color: '#6b7280',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setGroupToRename(group.id);
                          setNewGroupName(group.name);
                        }}
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        style={{
                          padding: '0.375rem',
                          borderRadius: '0.25rem',
                          border: 'none',
                          backgroundColor: hoverGroup === group.id ? '#f3f4f6' : 'transparent',
                          color: group.status === 'active' ? '#6b7280' : '#10b981',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          // In a real implementation, this would update the group status via API
                          const updatedGroups = groups.map(g => {
                            if (g.id === group.id) {
                              return {
                                ...g,
                                status: g.status === 'active' ? 'inactive' : 'active'
                              };
                            }
                            return g;
                          });
                          setGroups(updatedGroups);
                        }}
                      >
                        {group.status === 'active' ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                      <button
                        style={{
                          padding: '0.375rem',
                          borderRadius: '0.25rem',
                          border: 'none',
                          backgroundColor: hoverGroup === group.id ? '#fee2e2' : 'transparent',
                          color: hoverGroup === group.id ? '#ef4444' : '#6b7280',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedGroup(group);
                          setIsDeleteModalOpen(true);
                        }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          {currentGroups.length === 0 && (
            <tfoot>
              <tr>
                <td colSpan={Object.values(tableColumns).filter(Boolean).length} style={{ 
                  padding: '2rem 1rem',
                  textAlign: 'center',
                  color: '#6b7280'
                }}>
                  <div style={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <Users size={24} color="#d1d5db" />
                    <p style={{ margin: 0, fontSize: '0.875rem' }}>
                      No groups found
                    </p>
                  </div>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    );
  };

  // ========== RENDER MAIN COMPONENTS ==========
  const renderActionBar = () => {
    return (
      <div 
        className="action-bar"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '1rem',
          backgroundColor: '#ffffff',
          borderRadius: '0.5rem',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          marginBottom: '1rem',
          border: '1px solid #e5e7eb',
          flexWrap: 'wrap',
          gap: '0.75rem'
        }}
      >
        <div className="search-filter" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
          {/* Search bar */}
          <div className="search-container" style={{ position: 'relative', flex: 1, maxWidth: '24rem' }}>
            <Search 
              size={18} 
              style={{ 
                position: 'absolute', 
                left: '0.75rem', 
                top: '50%', 
                transform: 'translateY(-50%)',
                color: '#6b7280' 
              }} 
            />
            <input
              type="text"
              placeholder="Search groups..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              style={{
                width: '100%',
                padding: '0.625rem 1rem 0.625rem 2.5rem',
                borderRadius: '0.375rem',
                border: '1px solid #e5e7eb',
                fontSize: '0.875rem',
                outline: 'none',
                transition: 'all 0.2s ease',
                backgroundColor: '#f9fafb',
                color: '#111827',
              }}
            />
            {searchValue && (
              <button
                style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '0.25rem'
                }}
                onClick={() => setSearchValue('')}
              >
                <X size={16} />
              </button>
            )}
          </div>
          
          {/* Filter button */}
          <div className="filter-container" style={{ position: 'relative' }}>
            <button
              className={activeAnimation === 'filter-button' ? 'pulse' : ''}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.625rem 1rem',
                backgroundColor: isFilterMenuOpen ? '#f3f4f6' : '#ffffff',
                color: '#6b7280',
                border: '1px solid #e5e7eb',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: 'medium',
                transition: 'all 0.2s ease',
                animation: activeAnimation === 'filter-button' ? 'pulse 0.7s cubic-bezier(0.4, 0, 0.6, 1)' : 'none'
              }}
              onClick={() => {
                setIsFilterMenuOpen(!isFilterMenuOpen);
                pulseAnimation('filter-button');
              }}
            >
              <Filter size={16} />
              Filters
              {(filterOptions.memberCount !== 'all' || filterOptions.status !== 'all') && (
                <span className="filter-badge" style={{
                  width: '1.25rem',
                  height: '1.25rem',
                  backgroundColor: accentColor,
                  color: '#ffffff',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  fontWeight: '600'
                }}>
                  {Object.values(filterOptions).filter(value => value !== 'all').length}
                </span>
              )}
            </button>
            
            {isFilterMenuOpen && (
              <div
                className="filter-menu"
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: '0',
                  zIndex: 10,
                  marginTop: '0.5rem',
                  backgroundColor: '#ffffff',
                  borderRadius: '0.5rem',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #e5e7eb',
                  padding: '1rem',
                  width: '16rem'
                }}
              >
                <div style={{ marginBottom: '1rem' }}>
                  <h3 style={{ 
                    margin: '0 0 0.75rem 0', 
                    fontSize: '0.875rem', 
                    fontWeight: '600',
                    color: '#111827'
                  }}>
                    Member Count
                  </h3>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {['all', 'small', 'medium', 'large'].map(option => (
                      <button
                        key={option}
                        style={{
                          padding: '0.375rem 0.75rem',
                          borderRadius: '0.25rem',
                          border: '1px solid #e5e7eb',
                          backgroundColor: filterOptions.memberCount === option ? accentColor : '#ffffff',
                          color: filterOptions.memberCount === option ? '#ffffff' : '#6b7280',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={() => handleFilterChange('memberCount', option)}
                      >
                        {option === 'all' ? 'All' : option === 'small' ? '< 5' : option === 'medium' ? '5-10' : '10+'}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div style={{ marginBottom: '1rem' }}>
                  <h3 style={{ 
                    margin: '0 0 0.75rem 0', 
                    fontSize: '0.875rem', 
                    fontWeight: '600',
                    color: '#111827'
                  }}>
                    Status
                  </h3>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {['all', 'active', 'inactive'].map(option => (
                      <button
                        key={option}
                        style={{
                          padding: '0.375rem 0.75rem',
                          borderRadius: '0.25rem',
                          border: '1px solid #e5e7eb',
                          backgroundColor: filterOptions.status === option ? accentColor : '#ffffff',
                          color: filterOptions.status === option ? '#ffffff' : '#6b7280',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          textTransform: 'capitalize'
                        }}
                        onClick={() => handleFilterChange('status', option)}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  marginTop: '1rem',
                  paddingTop: '1rem',
                  borderTop: '1px solid #e5e7eb'
                }}>
                  <button
                    style={{
                      padding: '0.5rem 0.75rem',
                      borderRadius: '0.375rem',
                      border: 'none',
                      backgroundColor: '#f3f4f6',
                      color: '#6b7280',
                      fontSize: '0.75rem',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onClick={clearFilters}
                  >
                    Clear All
                  </button>
                  <button
                    style={{
                      padding: '0.5rem 0.75rem',
                      borderRadius: '0.375rem',
                      border: 'none',
                      backgroundColor: accentColor,
                      color: '#ffffff',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}
                    onClick={() => setIsFilterMenuOpen(false)}
                  >
                    <Check size={14} />
                    Apply Filters
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="action-buttons" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {/* View toggle */}
          <div className="view-toggle" style={{ 
            display: 'flex', 
            alignItems: 'center',
            padding: '0.25rem',
            backgroundColor: '#f3f4f6',
            borderRadius: '0.375rem',
            border: '1px solid #e5e7eb',
          }}>
            <button
              style={{
                padding: '0.375rem 0.75rem',
                borderRadius: '0.25rem',
                border: 'none',
                backgroundColor: view === 'list' ? '#ffffff' : 'transparent',
                color: view === 'list' ? '#111827' : '#6b7280',
                fontSize: '0.75rem',
                fontWeight: view === 'list' ? '600' : '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                boxShadow: view === 'list' ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none'
              }}
              onClick={() => triggerViewTransition('list')}
            >
              <AlignLeft size={14} />
              List
            </button>
            <button
              style={{
                padding: '0.375rem 0.75rem',
                borderRadius: '0.25rem',
                border: 'none',
                backgroundColor: view === 'table' ? '#ffffff' : 'transparent',
                color: view === 'table' ? '#111827' : '#6b7280',
                fontSize: '0.75rem',
                fontWeight: view === 'table' ? '600' : '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                boxShadow: view === 'table' ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none'
              }}
              onClick={() => triggerViewTransition('table')}
            >
              <FileText size={14} />
              Table
            </button>
            <button
              style={{
                padding: '0.375rem 0.75rem',
                borderRadius: '0.25rem',
                border: 'none',
                backgroundColor: view === 'grid' ? '#ffffff' : 'transparent',
                color: view === 'grid' ? '#111827' : '#6b7280',
                fontSize: '0.75rem',
                fontWeight: view === 'grid' ? '600' : '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                boxShadow: view === 'grid' ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none'
              }}
              onClick={() => triggerViewTransition('grid')}
            >
              <GitBranch size={14} />
              Grid
            </button>
          </div>
          
          {/* Refresh button */}
          <button
            className={activeAnimation === 'refresh-button' ? 'spin' : ''}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0.625rem',
              backgroundColor: '#ffffff',
              color: '#6b7280',
              border: '1px solid #e5e7eb',
              borderRadius: '0.375rem',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              animation: activeAnimation === 'refresh-button' ? 'spin 1s linear' : 'none'
            }}
            onClick={() => {
              refreshData();
              pulseAnimation('refresh-button');
            }}
          >
            <RefreshCw size={16} />
          </button>
          
          {/* Create group button */}
          <button
            className={activeAnimation === 'create-button' ? 'pulse' : ''}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.625rem 1rem',
              backgroundColor: accentColor,
              color: '#ffffff',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '600',
              transition: 'all 0.2s ease',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              animation: activeAnimation === 'create-button' ? 'pulse 0.7s cubic-bezier(0.4, 0, 0.6, 1)' : 'none'
            }}
            onClick={() => {
              setIsCreateModalOpen(true);
              pulseAnimation('create-button');
            }}
          >
            <PlusCircle size={16} />
            Create Group
          </button>
        </div>
      </div>
    );
  };

  // Stats cards
  const renderStatsCards = () => {
    return (
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '0.5rem',
            padding: '1.25rem',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
            border: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}
        >
          <div style={{
            width: '3rem',
            height: '3rem',
            borderRadius: '0.5rem',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#3b82f6'
          }}>
            <Users size={24} />
          </div>
          <div>
            <p style={{
              margin: '0 0 0.25rem 0',
              fontSize: '0.875rem',
              color: '#6b7280'
            }}>
              Total Groups
            </p>
            <h3 style={{
              margin: 0,
              fontSize: '1.5rem',
              fontWeight: '600',
              color: '#111827'
            }}>
              {stats.totalGroups}
            </h3>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '0.5rem',
            padding: '1.25rem',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
            border: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}
        >
          <div style={{
            width: '3rem',
            height: '3rem',
            borderRadius: '0.5rem',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#10b981'
          }}>
            <UserPlus size={24} />
          </div>
          <div>
            <p style={{
              margin: '0 0 0.25rem 0',
              fontSize: '0.875rem',
              color: '#6b7280'
            }}>
              Total Members
            </p>
            <h3 style={{
              margin: 0,
              fontSize: '1.5rem',
              fontWeight: '600',
              color: '#111827'
            }}>
              {stats.totalMembers}
            </h3>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '0.5rem',
            padding: '1.25rem',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
            border: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}
        >
          <div style={{
            width: '3rem',
            height: '3rem',
            borderRadius: '0.5rem',
            backgroundColor: 'rgba(249, 115, 22, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#f97316'
          }}>
            <UserMinus size={24} />
          </div>
          <div>
            <p style={{
              margin: '0 0 0.25rem 0',
              fontSize: '0.875rem',
              color: '#6b7280'
            }}>
              Unassigned Users
            </p>
            <h3 style={{
              margin: 0,
              fontSize: '1.5rem',
              fontWeight: '600',
              color: '#111827'
            }}>
              {stats.unassignedUsers}
            </h3>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.4 }}
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '0.5rem',
            padding: '1.25rem',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
            border: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}
        >
          <div style={{
            width: '3rem',
            height: '3rem',
            borderRadius: '0.5rem',
            backgroundColor: 'rgba(139, 92, 246, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#8b5cf6'
          }}>
            <Settings size={24} />
          </div>
          <div>
            <p style={{
              margin: '0 0 0.25rem 0',
              fontSize: '0.875rem',
              color: '#6b7280'
            }}>
              Avg Members/Group
            </p>
            <h3 style={{
              margin: 0,
              fontSize: '1.5rem',
              fontWeight: '600',
              color: '#111827'
            }}>
              {stats.avgMembersPerGroup.toFixed(1)}
            </h3>
          </div>
        </motion.div>
      </div>
    );
  };
  
  const renderGroupList = () => {
    return (
      <div className="group-list-container" style={{ marginTop: '1rem' }}>
        <AnimatePresence>
          {viewTransition && (
            <motion.div 
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(4px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 100
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1rem'
              }}>
                <div style={{
                  width: '2rem',
                  height: '2rem',
                  borderRadius: '50%',
                  borderTop: `2px solid ${accentColor}`,
                  borderRight: '2px solid transparent',
                  animation: 'spin 1s linear infinite'
                }} />
                <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  Changing view...
                </span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {view === 'list' && (
          <div 
            className="group-list"
            ref={scrollRef}
            style={{ 
              maxHeight: selectedGroup ? '30rem' : '100%',
              overflowY: 'auto',
              paddingRight: '0.5rem',
              transition: 'max-height 0.3s ease'
            }}
          >
            {currentGroups.map(group => renderGroupCard(group))}
            
            {/* Empty state */}
            {currentGroups.length === 0 && (
              <div 
                style={{
                  padding: '3rem',
                  textAlign: 'center',
                  backgroundColor: '#ffffff',
                  borderRadius: '0.5rem',
                  border: '1px dashed #e5e7eb'
                }}
              >
                <Users size={40} style={{ color: '#d1d5db', marginBottom: '1rem' }} />
                <h3 style={{ 
                  margin: '0 0 0.5rem 0', 
                  fontSize: '1rem', 
                  fontWeight: '600',
                  color: '#374151'
                }}>
                  No Groups Found
                </h3>
                <p style={{ 
                  margin: '0 0 1.5rem 0', 
                  fontSize: '0.875rem', 
                  color: '#6b7280',
                  maxWidth: '24rem',
                  marginLeft: 'auto',
                  marginRight: 'auto'
                }}>
                  {searchValue || filterOptions.memberCount !== 'all' || filterOptions.status !== 'all' 
                    ? 'No groups match your search criteria. Try adjusting your filters.'
                    : 'You haven\'t created any groups yet. Create your first group to get started.'}
                </p>
{(searchValue || filterOptions.memberCount !== 'all' || filterOptions.status !== 'all') && (
  <button
    style={{
      padding: '0.625rem 1rem',
      borderRadius: '0.375rem',
      border: 'none',
      backgroundColor: '#f3f4f6',
      color: '#6b7280',
      fontSize: '0.875rem',
      fontWeight: '500',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      margin: '0 auto'
    }}
    onClick={clearFilters}
  >
    <X size={16} />
    Clear Filters
  </button>
)}
{!searchValue && filterOptions.memberCount === 'all' && filterOptions.status === 'all' && (
  <button
    style={{
      padding: '0.625rem 1rem',
      borderRadius: '0.375rem',
      border: 'none',
      backgroundColor: accentColor,
      color: '#ffffff',
      fontSize: '0.875rem',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      margin: '0 auto'
    }}
    onClick={() => setIsCreateModalOpen(true)}
  >
    <PlusCircle size={16} />
    Create Group
  </button>
)}
</div>
)}

{/* Pagination */}
{currentGroups.length > 0 && totalPages > 1 && (
<div style={{
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.5rem',
  marginTop: '1.5rem',
  padding: '0.75rem',
  backgroundColor: '#ffffff',
  borderRadius: '0.5rem',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
  border: '1px solid #e5e7eb'
}}>
  <button
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0.5rem',
      borderRadius: '0.375rem',
      border: 'none',
      backgroundColor: currentPage === 1 ? '#f3f4f6' : '#ffffff',
      color: currentPage === 1 ? '#d1d5db' : '#6b7280',
      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
      boxShadow: currentPage === 1 ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.05)',
      border: '1px solid #e5e7eb'
    }}
    onClick={() => handlePageChange(currentPage - 1)}
    disabled={currentPage === 1}
  >
    <ChevronLeft size={16} />
  </button>
  
  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
    // Only show 5 pages at a time, with current page in the middle when possible
    const minPage = Math.max(1, currentPage - 2);
    const maxPage = Math.min(totalPages, minPage + 4);
    
    if (page < minPage || page > maxPage) {
      if (page === 1 && minPage > 1) {
        return (
          <React.Fragment key={page}>
            <button
              style={{
                padding: '0.5rem 0.75rem',
                borderRadius: '0.375rem',
                border: '1px solid #e5e7eb',
                backgroundColor: page === currentPage ? accentColor : '#ffffff',
                color: page === currentPage ? '#ffffff' : '#6b7280',
                fontSize: '0.875rem',
                fontWeight: page === currentPage ? '600' : '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: page === currentPage ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none'
              }}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
            <span style={{ color: '#d1d5db' }}>...</span>
          </React.Fragment>
        );
      }
      
      if (page === totalPages && maxPage < totalPages) {
        return (
          <React.Fragment key={page}>
            <span style={{ color: '#d1d5db' }}>...</span>
            <button
              style={{
                padding: '0.5rem 0.75rem',
                borderRadius: '0.375rem',
                border: '1px solid #e5e7eb',
                backgroundColor: page === currentPage ? accentColor : '#ffffff',
                color: page === currentPage ? '#ffffff' : '#6b7280',
                fontSize: '0.875rem',
                fontWeight: page === currentPage ? '600' : '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: page === currentPage ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none'
              }}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
          </React.Fragment>
        );
      }
      
      return null;
    }
    
    return (
      <button
        key={page}
        style={{
          padding: '0.5rem 0.75rem',
          borderRadius: '0.375rem',
          border: '1px solid #e5e7eb',
          backgroundColor: page === currentPage ? accentColor : '#ffffff',
          color: page === currentPage ? '#ffffff' : '#6b7280',
          fontSize: '0.875rem',
          fontWeight: page === currentPage ? '600' : '500',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          boxShadow: page === currentPage ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none'
        }}
        onClick={() => handlePageChange(page)}
      >
        {page}
      </button>
    );
  })}
  
  <button
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0.5rem',
      borderRadius: '0.375rem',
      border: 'none',
      backgroundColor: currentPage === totalPages ? '#f3f4f6' : '#ffffff',
      color: currentPage === totalPages ? '#d1d5db' : '#6b7280',
      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
      boxShadow: currentPage === totalPages ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.05)',
      border: '1px solid #e5e7eb'
    }}
    onClick={() => handlePageChange(currentPage + 1)}
    disabled={currentPage === totalPages}
  >
    <ChevronRight size={16} />
  </button>
</div>
)}
</div>
)}

{view === 'table' && renderTableView()}

{view === 'grid' && (
<div className="group-grid" style={{ 
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
  gap: '1rem' 
}}>
  {currentGroups.map(group => (
    <motion.div
      key={group.id}
      className={`group-grid-card ${dragOverGroup === group.id ? 'drag-over' : ''}`}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      style={{
        backgroundColor: '#ffffff',
        borderRadius: '0.5rem',
        overflow: 'hidden',
        border: '1px solid #e5e7eb',
        boxShadow: dragOverGroup === group.id 
          ? `0 0 0 2px ${group.colorCode || accentColor}, 0 4px 20px rgba(0, 0, 0, 0.1)` 
          : '0 1px 3px rgba(0, 0, 0, 0.05)',
        transition: 'all 0.3s ease',
        transform: dragOverGroup === group.id ? 'scale(1.02)' : 'scale(1)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer'
      }}
      onClick={() => handleGroupSelect(group)}
      onDragOver={(e) => handleDragOver(e, group.id)}
      onDrop={(e) => handleDrop(e, group.id)}
      onMouseEnter={() => setHoverGroup(group.id)}
      onMouseLeave={() => setHoverGroup(null)}
    >
      <div style={{ 
        height: '0.25rem', 
        backgroundColor: group.colorCode || accentColor,
        width: '100%'
      }} />
      
      <div style={{ 
        padding: '1rem',
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '0.75rem'
        }}>
          <h3 style={{ 
            margin: 0, 
            fontSize: '1rem', 
            fontWeight: '600',
            color: '#111827'
          }}>
            {group.name}
          </h3>
          {group.status && renderStatusBadge(group.status)}
        </div>
        
        <p style={{ 
          margin: '0 0 1rem 0',
          fontSize: '0.75rem',
          color: '#6b7280',
          flex: 1,
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          lineHeight: '1.5'
        }}>
          {group.description || 'No description available.'}
        </p>
        
        <div style={{
          padding: '0.75rem 0',
          borderTop: '1px solid #f3f4f6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
            fontSize: '0.75rem',
            color: '#6b7280'
          }}>
            <Users size={14} />
            <span>{group.memberCount || 0}</span>
          </div>
          
          <div style={{ 
            fontSize: '0.75rem',
            color: '#6b7280',
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem'
          }}>
            <Clock size={14} />
            <span>{formatDate(group.createdAt)}</span>
          </div>
        </div>
      </div>
    </motion.div>
  ))}
  
  {/* Add group card */}
  <motion.div
    className="add-group-card"
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.3, delay: 0.2 }}
    style={{
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
      border: '1px dashed #e5e7eb',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem 1rem',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      height: '100%',
      minHeight: '14rem'
    }}
    onClick={() => setIsCreateModalOpen(true)}
    whileHover={{ backgroundColor: '#f3f4f6' }}
  >
    <div style={{
      width: '3rem',
      height: '3rem',
      borderRadius: '50%',
      backgroundColor: `rgba(${parseInt(accentColor.slice(1, 3), 16)}, ${parseInt(accentColor.slice(3, 5), 16)}, ${parseInt(accentColor.slice(5, 7), 16)}, 0.1)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '1rem'
    }}>
      <PlusCircle size={24} color={accentColor} />
    </div>
    <p style={{
      margin: 0,
      fontSize: '0.875rem',
      fontWeight: '500',
      color: '#6b7280',
      textAlign: 'center'
    }}>
      Create New Group
    </p>
  </motion.div>
  
  {/* Empty state */}
  {currentGroups.length === 0 && (
    <motion.div
      className="empty-state"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      style={{
        gridColumn: '1 / -1',
        padding: '3rem',
        textAlign: 'center',
        backgroundColor: '#ffffff',
        borderRadius: '0.5rem',
        border: '1px dashed #e5e7eb'
      }}
    >
      <Users size={40} style={{ color: '#d1d5db', marginBottom: '1rem' }} />
      <h3 style={{ 
        margin: '0 0 0.5rem 0', 
        fontSize: '1rem', 
        fontWeight: '600',
        color: '#374151'
      }}>
        No Groups Found
      </h3>
      <p style={{ 
        margin: '0 0 1.5rem 0', 
        fontSize: '0.875rem', 
        color: '#6b7280',
        maxWidth: '24rem',
        marginLeft: 'auto',
        marginRight: 'auto'
      }}>
        {searchValue || filterOptions.memberCount !== 'all' || filterOptions.status !== 'all' 
          ? 'No groups match your search criteria. Try adjusting your filters.'
          : 'You haven\'t created any groups yet. Create your first group to get started.'}
      </p>
      {(searchValue || filterOptions.memberCount !== 'all' || filterOptions.status !== 'all') && (
        <button
          style={{
            padding: '0.625rem 1rem',
            borderRadius: '0.375rem',
            border: 'none',
            backgroundColor: '#f3f4f6',
            color: '#6b7280',
            fontSize: '0.875rem',
            fontWeight: '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            margin: '0 auto'
          }}
          onClick={clearFilters}
        >
          <X size={16} />
          Clear Filters
        </button>
      )}
    </motion.div>
  )}
</div>
)}
</div>
);
};

const renderGroupDetails = () => {
if (!selectedGroup) return null;

return (
<motion.div 
className="group-details"
initial={{ opacity: 0, y: 20 }}
animate={{ opacity: 1, y: 0 }}
transition={{ duration: 0.3 }}
style={{
backgroundColor: '#ffffff',
borderRadius: '0.5rem',
overflow: 'hidden',
boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
border: '1px solid #e5e7eb',
marginTop: '1rem'
}}
>
<div className="group-details-header" style={{
borderBottom: '1px solid #e5e7eb',
padding: '1rem',
display: 'flex',
justifyContent: 'space-between',
alignItems: 'center',
backgroundColor: selectedGroup.colorCode || accentColor,
color: '#ffffff',
position: 'relative',
overflow: 'hidden'
}}>
{/* Background pattern */}
<div style={{
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundImage: 'radial-gradient(circle at 20% 150%, rgba(255, 255, 255, 0.15) 5%, transparent 30%), radial-gradient(circle at 80% 50%, rgba(255, 255, 255, 0.1) 10%, transparent 40%)',
  opacity: 0.6,
  zIndex: 0
}} />

<div style={{ 
  display: 'flex', 
  alignItems: 'center', 
  gap: '0.75rem',
  position: 'relative',
  zIndex: 1
}}>
  <div style={{ 
    width: '2.5rem',
    height: '2.5rem',
    borderRadius: '0.5rem',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }}>
    <Users size={24} color="#ffffff" />
  </div>
  <div>
    <h2 style={{ 
      margin: '0 0 0.25rem 0', 
      fontSize: '1.25rem', 
      fontWeight: '600' 
    }}>
      {isEditMode ? (
        <input
          type="text"
          value={selectedGroup.name}
          onChange={(e) => setSelectedGroup({...selectedGroup, name: e.target.value})}
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '0.25rem',
            color: '#ffffff',
            padding: '0.25rem 0.5rem',
            fontSize: '1.125rem',
            fontWeight: '600',
            width: '100%',
            outline: 'none'
          }}
        />
      ) : (
        selectedGroup.name
      )}
    </h2>
    <div style={{ 
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      fontSize: '0.75rem',
      opacity: 0.9
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
        <Users size={14} />
        <span>{selectedGroup.members?.length || 0} members</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
        <Clock size={14} />
        <span>Created {formatDate(selectedGroup.createdAt)}</span>
      </div>
    </div>
  </div>
</div>

<div style={{ 
  display: 'flex', 
  gap: '0.5rem',
  position: 'relative',
  zIndex: 1
}}>
  {isEditMode ? (
    <>
      <button
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.5rem 0.75rem',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          color: '#6b7280',
          border: 'none',
          borderRadius: '0.375rem',
          cursor: 'pointer',
          fontSize: '0.75rem',
          fontWeight: '600',
          transition: 'all 0.2s ease'
        }}
        onClick={() => {
          // Reset the form by fetching the original data
          fetchGroupDetails(selectedGroup.id);
          setIsEditMode(false);
        }}
      >
        <X size={14} />
        Cancel
      </button>
      <button
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.5rem 0.75rem',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          color: '#10b981',
          border: 'none',
          borderRadius: '0.375rem',
          cursor: 'pointer',
          fontSize: '0.75rem',
          fontWeight: '600',
          transition: 'all 0.2s ease'
        }}
        onClick={handleUpdateGroup}
      >
        <Save size={14} />
        Save
      </button>
    </>
  ) : (
    <>
      <button
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.5rem 0.75rem',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          color: '#6b7280',
          border: 'none',
          borderRadius: '0.375rem',
          cursor: 'pointer',
          fontSize: '0.75rem',
          fontWeight: '600',
          transition: 'all 0.2s ease'
        }}
        onClick={() => setIsEditMode(true)}
      >
        <Edit size={14} />
        Edit
      </button>
      <button
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.5rem 0.75rem',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          color: '#ef4444',
          border: 'none',
          borderRadius: '0.375rem',
          cursor: 'pointer',
          fontSize: '0.75rem',
          fontWeight: '600',
          transition: 'all 0.2s ease'
        }}
        onClick={() => setIsDeleteModalOpen(true)}
      >
        <Trash2 size={14} />
        Delete
      </button>
      <button
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0.5rem',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          color: '#6b7280',
          border: 'none',
          borderRadius: '0.375rem',
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        }}
        onClick={() => setSelectedGroup(null)}
      >
        <X size={14} />
      </button>
    </>
  )}
</div>
</div>

{/* Tabs */}
<div className="group-details-tabs" style={{
display: 'flex',
borderBottom: '1px solid #e5e7eb',
backgroundColor: '#f9fafb'
}}>
<button
  style={{
    padding: '0.75rem 1.25rem',
    backgroundColor: selectedTab === 'info' ? '#ffffff' : 'transparent',
    color: selectedTab === 'info' ? '#111827' : '#6b7280',
    border: 'none',
    borderBottom: selectedTab === 'info' ? `2px solid ${selectedGroup.colorCode || accentColor}` : '2px solid transparent',
    fontSize: '0.875rem',
    fontWeight: selectedTab === 'info' ? '600' : '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  }}
  onClick={() => setSelectedTab('info')}
>
  <Info size={16} />
  Info
</button>
<button
  style={{
    padding: '0.75rem 1.25rem',
    backgroundColor: selectedTab === 'members' ? '#ffffff' : 'transparent',
    color: selectedTab === 'members' ? '#111827' : '#6b7280',
    border: 'none',
    borderBottom: selectedTab === 'members' ? `2px solid ${selectedGroup.colorCode || accentColor}` : '2px solid transparent',
    fontSize: '0.875rem',
    fontWeight: selectedTab === 'members' ? '600' : '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  }}
  onClick={() => setSelectedTab('members')}
>
  <Users size={16} />
  Members {selectedGroup.members?.length > 0 && `(${selectedGroup.members.length})`}
</button>
<button
  style={{
    padding: '0.75rem 1.25rem',
    backgroundColor: selectedTab === 'settings' ? '#ffffff' : 'transparent',
    color: selectedTab === 'settings' ? '#111827' : '#6b7280',
    border: 'none',
    borderBottom: selectedTab === 'settings' ? `2px solid ${selectedGroup.colorCode || accentColor}` : '2px solid transparent',
    fontSize: '0.875rem',
    fontWeight: selectedTab === 'settings' ? '600' : '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  }}
  onClick={() => setSelectedTab('settings')}
>
  <Settings size={16} />
  Settings
</button>
<button
  style={{
    padding: '0.75rem 1.25rem',
    backgroundColor: selectedTab === 'analytics' ? '#ffffff' : 'transparent',
    color: selectedTab === 'analytics' ? '#111827' : '#6b7280',
    border: 'none',
    borderBottom: selectedTab === 'analytics' ? `2px solid ${selectedGroup.colorCode || accentColor}` : '2px solid transparent',
    fontSize: '0.875rem',
    fontWeight: selectedTab === 'analytics' ? '600' : '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  }}
  onClick={() => setSelectedTab('analytics')}
>
  <BarChart2 size={16} />
  Analytics
</button>
</div>

{/* Tab content */}
<div className="group-details-content">
{/* Info tab */}
{selectedTab === 'info' && (
  <motion.div 
    className="group-info-tab"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.3 }}
    style={{ padding: '1.5rem' }}
  >
    <div className="group-description" style={{ marginBottom: '1.5rem' }}>
      <h3 style={{ 
        margin: '0 0 0.75rem 0', 
        fontSize: '0.875rem', 
        fontWeight: '600',
        color: '#4b5563',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        <FileText size={16} />
        Description
      </h3>
      {isEditMode ? (
        <textarea
          value={selectedGroup.description || ''}
          onChange={(e) => setSelectedGroup({...selectedGroup, description: e.target.value})}
          style={{
            width: '100%',
            padding: '0.75rem',
            borderRadius: '0.375rem',
            border: '1px solid #e5e7eb',
            fontSize: '0.875rem',
            color: '#111827',
            backgroundColor: '#f9fafb',
            minHeight: '6rem',
            resize: 'vertical',
            outline: 'none'
          }}
        />
      ) : (
        <p style={{ 
          margin: 0, 
          fontSize: '0.875rem', 
          color: '#6b7280',
          lineHeight: '1.5',
          backgroundColor: '#f9fafb',
          padding: '0.75rem',
          borderRadius: '0.375rem',
          border: '1px solid #e5e7eb'
        }}>
          {selectedGroup.description || 'No description available.'}
        </p>
      )}
    </div>
    
    <div className="group-activity-log" style={{ marginBottom: '1.5rem' }}>
      <h3 style={{ 
        margin: '0 0 0.75rem 0', 
        fontSize: '0.875rem', 
        fontWeight: '600',
        color: '#4b5563',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        <Clock size={16} />
        Recent Activity
      </h3>
      <div style={{ 
        backgroundColor: '#f9fafb',
        borderRadius: '0.375rem',
        border: '1px solid #e5e7eb',
        maxHeight: '15rem',
        overflowY: 'auto'
      }}>
        {activityLog.length === 0 ? (
          <div style={{ 
            padding: '1.5rem', 
            textAlign: 'center',
            color: '#6b7280',
            fontSize: '0.875rem'
          }}>
            No recent activity found
          </div>
        ) : (
          <table style={{ 
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.75rem'
          }}>
            <thead style={{
              backgroundColor: '#f3f4f6',
              color: '#4b5563',
              textAlign: 'left',
              fontWeight: '600',
              position: 'sticky',
              top: 0,
              zIndex: 1
            }}>
              <tr>
                <th style={{ padding: '0.625rem 1rem', whiteSpace: 'nowrap' }}>Action</th>
                <th style={{ padding: '0.625rem 1rem', whiteSpace: 'nowrap' }}>User</th>
                <th style={{ padding: '0.625rem 1rem', whiteSpace: 'nowrap' }}>Target</th>
                <th style={{ padding: '0.625rem 1rem', whiteSpace: 'nowrap' }}>When</th>
              </tr>
            </thead>
            <tbody>
              {activityLog.map(activity => (
                <tr 
                  key={activity.id}
                  style={{
                    backgroundColor: '#ffffff',
                    borderBottom: '1px solid #f3f4f6'
                  }}
                >
                  <td style={{ 
                    padding: '0.5rem 1rem',
                    color: '#111827',
                    fontWeight: '500'
                  }}>
                    {activity.action}
                  </td>
                  <td style={{ 
                    padding: '0.5rem 1rem',
                    color: '#4b5563'
                  }}>
                    {activity.user}
                  </td>
                  <td style={{ 
                    padding: '0.5rem 1rem',
                    color: '#4b5563'
                  }}>
                    {activity.target}
                  </td>
                  <td style={{ 
                    padding: '0.5rem 1rem',
                    color: '#6b7280',
                    whiteSpace: 'nowrap'
                  }}>
                    {formatTime(activity.timestamp)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
    
    <div className="group-details-section" style={{ marginBottom: '1.5rem' }}>
      <h3 style={{ 
        margin: '0 0 0.75rem 0', 
        fontSize: '0.875rem', 
        fontWeight: '600',
        color: '#4b5563',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        <Info size={16} />
        Group Details
      </h3>
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(15rem, 1fr))',
        gap: '1rem'
      }}>
        <div style={{ 
          backgroundColor: '#f9fafb',
          padding: '0.75rem',
          borderRadius: '0.375rem',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ 
            fontSize: '0.75rem',
            color: '#6b7280',
            marginBottom: '0.25rem'
          }}>
            Status
          </div>
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            {isEditMode ? (
              <select
                value={selectedGroup.status || 'active'}
                onChange={(e) => setSelectedGroup({...selectedGroup, status: e.target.value})}
                style={{
                  padding: '0.25rem 0.5rem',
                  borderRadius: '0.25rem',
                  border: '1px solid #e5e7eb',
                  fontSize: '0.875rem',
                  backgroundColor: '#ffffff',
                  color: '#111827',
                  outline: 'none'
                }}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            ) : (
              renderStatusBadge(selectedGroup.status || 'active')
            )}
          </div>
        </div>
        
        <div style={{ 
          backgroundColor: '#f9fafb',
          padding: '0.75rem',
          borderRadius: '0.375rem',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ 
            fontSize: '0.75rem',
            color: '#6b7280',
            marginBottom: '0.25rem'
          }}>
            Created
          </div>
          <div style={{ 
            fontSize: '0.875rem',
            color: '#111827',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <Clock size={14} color="#6b7280" />
            {formatDate(selectedGroup.createdAt)}
          </div>
        </div>
        
        <div style={{ 
          backgroundColor: '#f9fafb',
          padding: '0.75rem',
          borderRadius: '0.375rem',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ 
            fontSize: '0.75rem',
            color: '#6b7280',
            marginBottom: '0.25rem'
          }}>
            Color
          </div>
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            {isEditMode ? (
              <div style={{ position: 'relative' }}>
                <button
                  style={{
                    padding: '0.25rem',
                    borderRadius: '0.25rem',
                    width: '6rem',
                    height: '1.75rem',
                    border: '1px solid #e5e7eb',
                    backgroundColor: selectedGroup.colorCode || accentColor,
                    cursor: 'pointer'
                  }}
                  onClick={() => setColorPickerOpen(!colorPickerOpen)}
                />
                
                {colorPickerOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      zIndex: 10,
                      marginTop: '0.25rem',
                      backgroundColor: '#ffffff',
                      borderRadius: '0.375rem',
                      border: '1px solid #e5e7eb',
                      padding: '0.5rem',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(4, 1fr)',
                      gap: '0.25rem',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    {[
                      '#e6376e', '#ef4444', '#f97316', '#f59e0b',
                      '#10b981', '#14b8a6', '#0ea5e9', '#3b82f6',
                      '#8b5cf6', '#a855f7', '#ec4899', '#f43f5e'
                    ].map(color => (
                      <button
                        key={color}
                        style={{
                          width: '1.5rem',
                          height: '1.5rem',
                          borderRadius: '0.25rem',
                          backgroundColor: color,
                          border: color === selectedGroup.colorCode ? '2px solid #111827' : '1px solid #e5e7eb',
                          cursor: 'pointer'
                        }}
                        onClick={() => {
                          setSelectedGroup({...selectedGroup, colorCode: color});
                          setColorPickerOpen(false);
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <>
                <div
                  style={{
                    width: '1rem',
                    height: '1rem',
                    borderRadius: '0.25rem',
                    backgroundColor: selectedGroup.colorCode || accentColor
                  }}
                />
                <span style={{ 
                  fontSize: '0.875rem',
                  color: '#111827',
                  fontWeight: '500'
                }}>
                  {selectedGroup.colorCode || accentColor}
                </span>
              </>
            )}
          </div>
        </div>
        
        <div style={{ 
          backgroundColor: '#f9fafb',
          padding: '0.75rem',
          borderRadius: '0.375rem',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ 
            fontSize: '0.75rem',
            color: '#6b7280',
            marginBottom: '0.25rem'
          }}>
            Member Limit
          </div>
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            {isEditMode ? (
              <input
                type="number"
                value={selectedGroup.membersLimit || 10}
                onChange={(e) => setSelectedGroup({...selectedGroup, membersLimit: parseInt(e.target.value, 10)})}
                min={(selectedGroup.members?.length || 0)}
                max={50}
                step={1}
                style={{
                  padding: '0.25rem 0.5rem',
                  borderRadius: '0.25rem',
                  border: '1px solid #e5e7eb',
                  fontSize: '0.875rem',
                  backgroundColor: '#ffffff',
                  color: '#111827',
                  width: '4rem',
                  outline: 'none'
                }}
              />
            ) : (
              <span style={{ 
                fontSize: '0.875rem',
                color: '#111827',
                fontWeight: '500'
              }}>
                {selectedGroup.membersLimit || 10} members
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  </motion.div>
)}

{/* Members tab */}
{selectedTab === 'members' && (
  <motion.div 
    className="group-members-tab"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.3 }}
    style={{ padding: '1.5rem' }}
  >
    <div style={{ 
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1rem'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <h3 style={{ 
          margin: 0, 
          fontSize: '0.875rem', 
          fontWeight: '600',
          color: '#4b5563'
        }}>
          Members ({selectedGroup.members?.length || 0}/{selectedGroup.membersLimit || 10})
        </h3>
        
        {(selectedGroup.members?.length || 0) >= (selectedGroup.membersLimit || 10) && (
          <span style={{
            padding: '0.25rem 0.5rem',
            backgroundColor: '#fff7ed',
            color: '#f97316',
            borderRadius: '0.25rem',
            fontSize: '0.75rem',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
            border: '1px solid #fed7aa'
          }}>
            <AlertTriangle size={12} />
            Limit Reached
          </span>
        )}
      </div>
      
      <div style={{ 
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        <div style={{ position: 'relative' }}>
          <div style={{
            position: 'relative'
          }}>
            <Search 
              size={16} 
              style={{ 
                position: 'absolute', 
                left: '0.5rem', 
                top: '50%', 
                transform: 'translateY(-50%)',
                color: '#6b7280' 
              }} 
            />
            <input
              type="text"
              placeholder="Search members..."
              value={memberSearchValue}
              onChange={(e) => setMemberSearchValue(e.target.value)}
              style={{
                padding: '0.5rem 0.5rem 0.5rem 2rem',
                borderRadius: '0.375rem',
                border: '1px solid #e5e7eb',
                fontSize: '0.75rem',
                outline: 'none',
                width: '12rem'
              }}
            />
            {memberSearchValue && (
              <button
                style={{
                  position: 'absolute',
                  right: '0.5rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  color: '#6b7280'
                }}
                onClick={() => setMemberSearchValue('')}
              >
                <X size={12} />
              </button>
            )}
          </div>
        </div>
        
        <button
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 0.75rem',
            backgroundColor: (selectedGroup.members?.length || 0) >= (selectedGroup.membersLimit || 10)
              ? '#f3f4f6' 
              : selectedGroup.colorCode || accentColor,
            color: (selectedGroup.members?.length || 0) >= (selectedGroup.membersLimit || 10)
              ? '#6b7280' 
              : '#ffffff',
            border: 'none',
            borderRadius: '0.375rem',
            cursor: (selectedGroup.members?.length || 0) >= (selectedGroup.membersLimit || 10)
              ? 'not-allowed' 
              : 'pointer',
            fontSize: '0.75rem',
            fontWeight: '600',
            transition: 'all 0.2s ease'
          }}
          onClick={() => {
            if ((selectedGroup.members?.length || 0) < (selectedGroup.membersLimit || 10)) {
              setAddMemberMode(!addMemberMode);
            }
          }}
          disabled={(selectedGroup.members?.length || 0) >= (selectedGroup.membersLimit || 10)}
        >
          {addMemberMode ? (
            <>
              <X size={14} />
              Cancel
            </>
          ) : (
            <>
              <UserPlus size={14} />
              Add Member
            </>
          )}
        </button>
      </div>
    </div>
    
    {addMemberMode && (
      <motion.div 
        className="add-member-section"
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: 'auto' }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.3 }}
        style={{
          backgroundColor: '#f9fafb',
          borderRadius: '0.375rem',
          border: '1px solid #e5e7eb',
          padding: '1rem',
          marginBottom: '1rem',
          overflow: 'hidden'
        }}
      >
        <h4 style={{ 
          margin: '0 0 0.75rem 0', 
          fontSize: '0.875rem', 
          fontWeight: '600',
          color: '#4b5563',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <UserPlus size={16} />
          Add Member to {selectedGroup.name}
        </h4>
        
        {isMemberLoading ? (
          <div style={{ 
            padding: '1rem', 
            textAlign: 'center',
            color: '#6b7280'
          }}>
            <div style={{
              width: '1.5rem',
              height: '1.5rem',
              borderRadius: '50%',
              borderTop: `2px solid ${selectedGroup.colorCode || accentColor}`,
              borderRight: '2px solid transparent',
              margin: '0 auto 0.5rem',
              animation: 'spin 1s linear infinite'
            }} />
            <p style={{ margin: 0, fontSize: '0.875rem' }}>
              Processing...
            </p>
          </div>
        ) : (
          <>
            {filteredAvailableUsers.length === 0 ? (
              <div style={{ 
                padding: '1rem', 
                textAlign: 'center',
                color: '#6b7280'
              }}>
                {memberSearchValue ? (
                  <p style={{ margin: 0, fontSize: '0.875rem' }}>
                    No users match your search
                  </p>
                ) : (
                  <p style={{ margin: 0, fontSize: '0.875rem' }}>
                    No users available to add
                  </p>
                )}
              </div>
            ) : (
              <div style={{ maxHeight: '15rem', overflowY: 'auto' }}>
                {filteredAvailableUsers.map(user => (
                  <div
                    key={user.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0.5rem',
                      borderRadius: '0.375rem',
                      backgroundColor: '#ffffff',
                      marginBottom: '0.5rem',
                      border: '1px solid #e5e7eb'
                    }}
                  >
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem'
                    }}>
                      <div style={{ 
                        width: '2rem',
                        height: '2rem',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        flexShrink: 0
                      }}>
                        <img 
                          src={user.profileImagePath ? `${BASE_URL}${user.profileImagePath}` : `/api/placeholder/32/32`}
                          alt={user.username}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      </div>
                      <div>
                        <h5 style={{ 
                          margin: '0 0 0.25rem 0', 
                          fontSize: '0.875rem', 
                          fontWeight: '500',
                          color: '#111827'
                        }}>
                          {user.username}
                        </h5>
                        <p style={{ 
                          margin: 0, 
                          fontSize: '0.75rem', 
                          color: '#6b7280'
                        }}>
                          {user.email}
                        </p>
                      </div>
                    </div>
                    
                    <button
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.375rem 0.75rem',
                        backgroundColor: selectedGroup.colorCode || accentColor,
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '0.375rem',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => handleAddMember(user.id)}
                    >
                      <UserPlus size={14} />
                      Add
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </motion.div>
    )}
    
    <div className="members-list" style={{
      backgroundColor: '#f9fafb',
      borderRadius: '0.375rem',
      border: '1px solid #e5e7eb',
      overflow: 'hidden'
    }}>
      {filteredGroupMembers.length === 0 ? (
        <div style={{ 
          padding: '2rem', 
          textAlign: 'center',
          color: '#6b7280'
        }}>
          {memberSearchValue ? (
            <>
              <Search size={24} style={{ color: '#d1d5db', marginBottom: '0.5rem' }} />
              <p style={{ margin: 0, fontSize: '0.875rem' }}>
                No members match your search
              </p>
            </>
          ) : (
            <>
              <Users size={24} style={{ color: '#d1d5db', marginBottom: '0.5rem' }} />
              <p style={{ margin: 0, fontSize: '0.875rem' }}>
                This group has no members yet
              </p>
            </>
          )}
        </div>
      ) : (
        <table style={{ 
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '0.875rem'
        }}>
          <thead style={{
            backgroundColor: '#f3f4f6',
            color: '#4b5563',
            textAlign: 'left',
            fontWeight: '600'
          }}>
            <tr>
              <th style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap' }}>User</th>
              <th style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap' }}>Email</th>
              <th style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap' }}>Role</th>
              <th style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredGroupMembers.map(member => (
              <tr 
                key={member.id}
                draggable
                onDragStart={(e) => handleDragStart(e, member)}
                onDragEnd={handleDragEnd}
                style={{
                  backgroundColor: '#ffffff',
                  borderBottom: '1px solid #f3f4f6',
                  cursor: 'grab'
                }}
              >
                <td style={{ 
                  padding: '0.75rem 1rem',
                  color: '#111827'
                }}>
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem'
                  }}>
                    <div style={{ 
                      width: '2rem',
                      height: '2rem',
                      borderRadius: '50%',
                      overflow: 'hidden',
                      flexShrink: 0
                    }}>
                      <img 
                        src={member.profileImagePath ? `${BASE_URL}${member.profileImagePath}` : `/api/placeholder/32/32`}
                        alt={member.username}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    </div>
                    <span style={{ fontWeight: '500' }}>{member.username}</span>
                  </div>
                </td>
                <td style={{ 
                  padding: '0.75rem 1rem',
                  color: '#6b7280'
                }}>
                  {member.email}
                </td>
                <td style={{ 
                  padding: '0.75rem 1rem'
                }}>
                  <span style={{
                    display: 'inline-block',
                    padding: '0.25rem 0.5rem',
                    backgroundColor: member.role === 'ROLE_ADMIN' 
                      ? `rgba(${parseInt(selectedGroup.colorCode?.slice(1, 3) || accentColor.slice(1, 3), 16)}, ${parseInt(selectedGroup.colorCode?.slice(3, 5) || accentColor.slice(3, 5), 16)}, ${parseInt(selectedGroup.colorCode?.slice(5, 7) || accentColor.slice(5, 7), 16)}, 0.1)` 
                      : '#f3f4f6',
                    color: member.role === 'ROLE_ADMIN' 
                      ? selectedGroup.colorCode || accentColor
                      : '#6b7280',
                    borderRadius: '0.25rem',
                    fontSize: '0.75rem',
                    fontWeight: '500'
                  }}>
                    {member.role === 'ROLE_ADMIN' ? 'Admin' : 'Member'}
                  </span>
                </td>
                <td style={{ 
                  padding: '0.75rem 1rem',
                  textAlign: 'right'
                }}>
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: '0.5rem'
                  }}>
                    {member.role !== 'ROLE_ADMIN' && (
                      <button
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '0.375rem',
                          backgroundColor: '#f3f4f6',
                          color: '#6b7280',
                          border: 'none',
                          borderRadius: '0.375rem',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={async () => {
                          try {
                            const token = await localforage.getItem('token');
                            
                            const response = await fetch(`${BASE_URL}/api/groups/${selectedGroup.id}/members/${member.id}/role`, {
                              method: 'PUT',
                              headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                              },
                              body: JSON.stringify({ role: 'ROLE_ADMIN' })
                            });
                            
                            if (!response.ok) {
                              throw new Error('Failed to update member role');
                            }
                            
                            // Refresh the group
                            fetchGroupDetails(selectedGroup.id);
                            
                            // Show success message
                            animateSuccess(`${member.username} is now an admin`);
                          } catch (error) {
                            console.error('Error updating member role:', error);
                            setError(error.message || 'Failed to update member role');
                          }
                        }}
                      >
                        <Shield size={16} />
                      </button>
                    )}
                    
                    <button
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0.375rem',
                        backgroundColor: '#fee2e2',
                        color: '#ef4444',
                        border: 'none',
                        borderRadius: '0.375rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => handleRemoveMember(member.id)}
                    >
                      <UserMinus size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  </motion.div>
)}

{/* Settings tab */}
{selectedTab === 'settings' && (
  <motion.div 
    className="group-settings-tab"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.3 }}
    style={{ padding: '1.5rem' }}
  >
    <div className="settings-section" style={{ marginBottom: '1.5rem' }}>
      <h3 style={{ 
        margin: '0 0 1rem 0', 
        fontSize: '0.875rem', 
        fontWeight: '600',
        color: '#4b5563',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        <Settings size={16} />
        Group Settings
      </h3>
      
      <div style={{
        backgroundColor: '#f9fafb',
        borderRadius: '0.375rem',
        border: '1px solid #e5e7eb',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '1rem',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h4 style={{ 
              margin: '0 0 0.25rem 0', 
              fontSize: '0.875rem', 
              fontWeight: '600',
              color: '#111827'
            }}>
              Group Color
            </h4>
            <p style={{ 
              margin: 0, 
              fontSize: '0.75rem', 
              color: '#6b7280'
            }}>
              Change the color used to identify this group
            </p>
          </div>
          
          <div style={{ position: 'relative' }}>
            <button
              style={{
                padding: '0.25rem',
                borderRadius: '0.25rem',
                width: '6rem',
                height: '1.75rem',
                border: '1px solid #e5e7eb',
                backgroundColor: selectedGroup.colorCode || accentColor,
                cursor: 'pointer'
              }}
              onClick={() => setColorPickerOpen(!colorPickerOpen)}
            />
            
            {colorPickerOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  zIndex: 10,
                  marginTop: '0.25rem',
                  backgroundColor: '#ffffff',
                  borderRadius: '0.375rem',
                  border: '1px solid #e5e7eb',
                  padding: '0.5rem',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '0.25rem',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                }}
              >
                {[
                  '#e6376e', '#ef4444', '#f97316', '#f59e0b',
                  '#10b981', '#14b8a6', '#0ea5e9', '#3b82f6',
                  '#8b5cf6', '#a855f7', '#ec4899', '#f43f5e'
                ].map(color => (
                  <button
                    key={color}
                    style={{
                      width: '1.5rem',
                      height: '1.5rem',
                      borderRadius: '0.25rem',
                      backgroundColor: color,
                      border: color === selectedGroup.colorCode ? '2px solid #111827' : '1px solid #e5e7eb',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      setSelectedGroup({...selectedGroup, colorCode: color});
                      setColorPickerOpen(false);
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
        
        <div style={{
          padding: '1rem',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h4 style={{ 
              margin: '0 0 0.25rem 0', 
              fontSize: '0.875rem', 
              fontWeight: '600',
              color: '#111827'
            }}>
              Member Limit
            </h4>
            <p style={{ 
              margin: 0, 
              fontSize: '0.75rem', 
              color: '#6b7280'
            }}>
              Maximum number of members allowed in this group
            </p>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="number"
              value={selectedGroup.membersLimit || 10}
              onChange={(e) => {
                const value = parseInt(e.target.value, 10);
                if (value >= (selectedGroup.members?.length || 0)) {
                  setSelectedGroup({...selectedGroup, membersLimit: value});
                }
              }}
              min={(selectedGroup.members?.length || 0)}
              max={50}
              step={1}
              style={{
                padding: '0.5rem',
                borderRadius: '0.375rem',
                border: '1px solid #e5e7eb',
                fontSize: '0.875rem',
                backgroundColor: '#ffffff',
                color: '#111827',
                width: '5rem',
                outline: 'none'
              }}
            />
          </div>
        </div>
        
        <div style={{
          padding: '1rem',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h4 style={{ 
              margin: '0 0 0.25rem 0', 
              fontSize: '0.875rem', 
              fontWeight: '600',
              color: '#111827'
            }}>
              Group Status
            </h4>
            <p style={{ 
              margin: 0, 
              fontSize: '0.75rem', 
              color: '#6b7280'
            }}>
              Active groups are visible to all users
            </p>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 0.75rem',
                backgroundColor: selectedGroup.status === 'active' ? '#10b981' : '#f3f4f6',
                color: selectedGroup.status === 'active' ? '#ffffff' : '#6b7280',
                border: 'none',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontSize: '0.75rem',
                fontWeight: '500',
                transition: 'all 0.2s ease'
              }}
              onClick={() => setSelectedGroup({...selectedGroup, status: 'active'})}
            >
              <Eye size={14} />
              Active
            </button>
            <button
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 0.75rem',
                backgroundColor: selectedGroup.status === 'inactive' ? '#6b7280' : '#f3f4f6',
                color: selectedGroup.status === 'inactive' ? '#ffffff' : '#6b7280',
                border: 'none',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontSize: '0.75rem',
                fontWeight: '500',
                transition: 'all 0.2s ease'
              }}
              onClick={() => setSelectedGroup({...selectedGroup, status: 'inactive'})}
            >
              <EyeOff size={14} />
              Inactive
            </button>
          </div>
        </div>
        
        <div style={{
          padding: '1rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h4 style={{ 
              margin: '0 0 0.25rem 0', 
              fontSize: '0.875rem', 
              fontWeight: '600',
              color: '#111827'
            }}>
              Advanced Settings
            </h4>
            <p style={{ 
              margin: 0, 
              fontSize: '0.75rem', 
              color: '#6b7280'
            }}>
              Configure additional group settings
            </p>
          </div>
          
          <button
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 0.75rem',
              backgroundColor: '#f3f4f6',
              color: '#6b7280',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: 'pointer',
              fontSize: '0.75rem',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
            onClick={() => setIsAdvancedSettingsOpen(!isAdvancedSettingsOpen)}
          >
            <Settings size={14} />
            {isAdvancedSettingsOpen ? 'Hide' : 'Show'}
          </button>
        </div>
        
        <AnimatePresence>
          {isAdvancedSettingsOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              style={{
                borderTop: '1px solid #e5e7eb',
                padding: '1rem',
                overflow: 'hidden'
              }}
            >
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '0.5rem'
                }}>
                  <span style={{ 
                    fontSize: '0.75rem', 
                    fontWeight: '500',
                    color: '#111827'
                  }}>
                    Allow Member Invitations
                  </span>
                  <div style={{ 
                    width: '2.25rem',
                    height: '1.25rem',
                    backgroundColor: '#10b981',
                    borderRadius: '9999px',
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease'
                  }}>
                    <div style={{ 
                      width: '0.875rem',
                      height: '0.875rem',
                      backgroundColor: '#ffffff',
                      borderRadius: '50%',
                      position: 'absolute',
                      top: '0.1875rem',
                      right: '0.1875rem',
                      transition: 'transform 0.2s ease'
                    }} />
                  </div>
                </label>
                <p style={{ 
                  margin: 0, 
                  fontSize: '0.75rem', 
                  color: '#6b7280'
                }}>
                  Allow group members to invite others
                </p>
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '0.5rem'
                }}>
                  <span style={{ 
                    fontSize: '0.75rem', 
                    fontWeight: '500',
                    color: '#111827'
                  }}>
                    Allow Member Removal
                  </span>
                  <div style={{ 
                    width: '2.25rem',
                    height: '1.25rem',
                    backgroundColor: '#e5e7eb',
                    borderRadius: '9999px',
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease'
                  }}>
                    <div style={{ 
                      width: '0.875rem',
                      height: '0.875rem',
                      backgroundColor: '#ffffff',
                      borderRadius: '50%',
                      position: 'absolute',
                      top: '0.1875rem',
                      left: '0.1875rem',
                      transition: 'transform 0.2s ease'
                    }} />
                  </div>
                </label>
                <p style={{ 
                  margin: 0, 
                  fontSize: '0.75rem', 
                  color: '#6b7280'
                }}>
                  Allow group members to remove others
                </p>
              </div>
              
              <div>
                <label style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '0.5rem'
                }}>
                  <span style={{ 
                    fontSize: '0.75rem', 
                    fontWeight: '500',
                    color: '#111827'
                  }}>
                    Group Visibility
                  </span>
                  <select
                    value="public"
                    style={{
                      padding: '0.375rem 0.5rem',
                      borderRadius: '0.375rem',
                      border: '1px solid #e5e7eb',
                      fontSize: '0.75rem',
                      backgroundColor: '#ffffff',
                      color: '#111827',
                      outline: 'none'
                    }}
                  >
                    <option value="public">Public</option>
                    <option value="protected">Protected</option>
                    <option value="private">Private</option>
                  </select>
                </label>
                <p style={{ 
                  margin: 0, 
                  fontSize: '0.75rem', 
                  color: '#6b7280'
                }}>
                  Control who can see this group
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
    
    <div className="danger-zone" style={{ marginTop: '2rem' }}>
      <h3 style={{ 
        margin: '0 0 1rem 0', 
        fontSize: '0.875rem', 
        fontWeight: '600',
        color: '#ef4444',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        <AlertTriangle size={16} />
        Danger Zone
      </h3>
      
      <div style={{
        backgroundColor: '#fff5f5',
        borderRadius: '0.375rem',
        border: '1px solid #fecaca',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '1rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h4 style={{ 
              margin: '0 0 0.25rem 0', 
              fontSize: '0.875rem', 
              fontWeight: '600',
              color: '#111827'
            }}>
              Delete Group
            </h4>
            <p style={{ 
              margin: 0, 
              fontSize: '0.75rem', 
              color: '#6b7280'
            }}>
              Permanently delete this group and all of its data
            </p>
          </div>
          
          <button
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 0.75rem',
              backgroundColor: '#ef4444',
              color: '#ffffff',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: 'pointer',
              fontSize: '0.75rem',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
            onClick={() => setIsDeleteModalOpen(true)}
          >
            <Trash2 size={14} />
            Delete Group
          </button>
        </div>
      </div>
    </div>
  </motion.div>
)}

{/* Analytics tab */}
{selectedTab === 'analytics' && (
  <motion.div 
    className="group-analytics-tab"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.3 }}
    style={{ padding: '1.5rem' }}
  >
    {isAnalyticsLoading ? (
      <div style={{ 
        padding: '3rem', 
        textAlign: 'center',
        color: '#6b7280'
      }}>
        <div style={{
          width: '2rem',
          height: '2rem',
          borderRadius: '50%',
          borderTop: `2px solid ${selectedGroup.colorCode || accentColor}`,
          borderRight: '2px solid transparent',
          margin: '0 auto 1rem',
          animation: 'spin 1s linear infinite'
        }} />
        <p style={{ 
          margin: '0 0 0.25rem 0', 
          fontSize: '1rem', 
          fontWeight: '500',
          color: '#111827'
        }}>
          Loading Analytics
        </p>
        <p style={{ 
          margin: 0, 
          fontSize: '0.875rem'
        }}>
          Please wait while we gather group data...
        </p>
      </div>
    ) : (
      <>
        <div style={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(12rem, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{ 
            backgroundColor: '#ffffff',
            borderRadius: '0.375rem',
            border: '1px solid #e5e7eb',
            padding: '1rem',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{ 
              fontSize: '0.75rem',
              color: '#6b7280',
              marginBottom: '0.5rem'
            }}>
              Total Members
            </div>
            <div style={{ 
              fontSize: '1.5rem',
              fontWeight: '600',
              color: '#111827',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              {groupAnalytics.totalMembers}
              <span style={{ 
                fontSize: '0.75rem',
                fontWeight: '500',
                color: groupAnalytics.memberGrowth > 0 ? '#10b981' : '#ef4444',
                backgroundColor: groupAnalytics.memberGrowth > 0 ? '#d1fae5' : '#fee2e2',
                padding: '0.25rem 0.5rem',
                borderRadius: '9999px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem'
              }}>
                {groupAnalytics.memberGrowth > 0 ? '+' : ''}{groupAnalytics.memberGrowth}%
              </span>
            </div>
          </div>
          
          <div style={{ 
            backgroundColor: '#ffffff',
            borderRadius: '0.375rem',
            border: '1px solid #e5e7eb',
            padding: '1rem',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{ 
              fontSize: '0.75rem',
              color: '#6b7280',
              marginBottom: '0.5rem'
            }}>
              Activities This Month
            </div>
            <div style={{ 
              fontSize: '1.5rem',
              fontWeight: '600',
              color: '#111827'
            }}>
              {groupAnalytics.activitiesThisMonth}
            </div>
          </div>
          
          <div style={{ 
            backgroundColor: '#ffffff',
            borderRadius: '0.375rem',
            border: '1px solid #e5e7eb',
            padding: '1rem',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
          }}>
            <div style={{ 
              fontSize: '0.75rem',
              color: '#6b7280',
              marginBottom: '0.5rem'
            }}>
              Completion Rate
            </div>
            <div style={{ 
              fontSize: '1.5rem',
              fontWeight: '600',
              color: '#111827'
            }}>
              {groupAnalytics.completionRate}%
            </div>
          </div>
        </div>
        
        <div style={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(20rem, 1fr))',
          gap: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{ 
            backgroundColor: '#ffffff',
            borderRadius: '0.375rem',
            border: '1px solid #e5e7eb',
            padding: '1rem',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
          }}>
            <h3 style={{ 
              margin: '0 0 1rem 0', 
              fontSize: '0.875rem', 
              fontWeight: '600',
              color: '#4b5563',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <BarChart2 size={16} />
              Activity by Day
            </h3>
            
            <div style={{ 
              display: 'flex',
              alignItems: 'flex-end',
              height: '12rem',
              gap: '0.5rem',
              padding: '0 0.5rem'
            }}>
              {groupAnalytics.activityByDay?.map((day, index) => (
                <div
                  key={day.day}
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      height: `${(day.count / 10) * 10}rem`,
                      backgroundColor: index % 2 === 0 ? selectedGroup.colorCode || accentColor : adjustColor(selectedGroup.colorCode || accentColor, 20),
                      borderRadius: '0.25rem',
                      transition: 'height 0.5s ease'
                    }}
                  />
                  <div style={{ 
                    fontSize: '0.75rem',
                    color: '#6b7280',
                    fontWeight: '500'
                  }}>
                    {day.day}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div style={{ 
            backgroundColor: '#ffffff',
            borderRadius: '0.375rem',
            border: '1px solid #e5e7eb',
            padding: '1rem',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
          }}>
            <h3 style={{ 
              margin: '0 0 1rem 0', 
              fontSize: '0.875rem', 
              fontWeight: '600',
              color: '#4b5563',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Users size={16} />
              Top Contributors
            </h3>
            
            {groupAnalytics.topContributors?.length > 0 ? (
              <div style={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem'
              }}>
                {groupAnalytics.topContributors.map((contributor, index) => (
                  <div
                    key={contributor.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.75rem',
                      backgroundColor: '#f9fafb',
                      borderRadius: '0.375rem',
                      border: '1px solid #e5e7eb'
                    }}
                  >
                    <div style={{ 
                      width: '2.5rem',
                      height: '2.5rem',
                      borderRadius: '50%',
                      overflow: 'hidden',
                      flexShrink: 0,
                      border: `2px solid ${
                        index === 0 ? '#f59e0b' : 
                        index === 1 ? '#6b7280' : 
                        '#a8551a'
                      }`
                    }}>
                      <img 
                        src={contributor.profileImagePath ? `${BASE_URL}${contributor.profileImagePath}` : `/api/placeholder/40/40`}
                        alt={contributor.username}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    </div>
                    
                    <div style={{ flex: 1 }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '0.25rem'
                      }}>
                        <h4 style={{ 
                          margin: 0,
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          color: '#111827'
                        }}>
                          {contributor.username}
                        </h4>
                        <span style={{ 
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          color: '#6b7280'
                        }}>
                          {contributor.contributions} contributions
                        </span>
                      </div>
                      
                      <div style={{ 
                        width: '100%',
                        height: '0.375rem',
                        backgroundColor: '#e5e7eb',
                        borderRadius: '9999px',
                        overflow: 'hidden'
                      }}>
                        <div
                          style={{
                            height: '100%',
                            width: `${(contributor.contributions / 20) * 100}%`,
                            backgroundColor: selectedGroup.colorCode || accentColor,
                            borderRadius: '9999px',
                            transition: 'width 0.5s ease'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ 
                padding: '2rem', 
                textAlign: 'center',
                color: '#6b7280'
              }}>
                <p style={{ margin: 0, fontSize: '0.875rem' }}>
                  No contributor data available
                </p>
              </div>
            )}
          </div>
        </div>
      </>
    )}
  </motion.div>
)}
</div>
</motion.div>
);
};

// ========== RENDER MODALS ==========
const renderCreateModal = () => {
if (!isCreateModalOpen) return null;

return (
<div className="modal-overlay" style={{
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
  backdropFilter: 'blur(4px)'
}}>
  <motion.div 
    className="create-modal"
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.3 }}
    style={{
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      width: '100%',
      maxWidth: '32rem',
      maxHeight: '90vh',
      overflow: 'auto',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
      display: 'flex',
      flexDirection: 'column'
    }}
  >
    <div className="modal-header" style={{
      padding: '1.25rem',
      borderBottom: '1px solid #e5e7eb',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      position: 'sticky',
      top: 0,
      backgroundColor: '#ffffff',
      zIndex: 1
    }}>
      <h2 style={{ 
        margin: 0, 
        fontSize: '1.125rem', 
        fontWeight: '600',
        color: '#111827'
      }}>
        Create New Group
      </h2>
      <button
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '2rem',
          height: '2rem',
          borderRadius: '0.375rem',
          border: 'none',
          backgroundColor: '#f3f4f6',
          color: '#6b7280',
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        }}
        onClick={() => setIsCreateModalOpen(false)}
      >
        <X size={20} />
      </button>
    </div>
    
    <div className="modal-body" style={{ padding: '1.5rem' }}>
      <div className="form-group" style={{ marginBottom: '1.5rem' }}>
        <label style={{ 
          display: 'block', 
          fontSize: '0.875rem',
          fontWeight: '500',
          color: '#111827',
          marginBottom: '0.5rem'
        }}>
          Group Name
        </label>
        <input
          type="text"
          value={newGroup.name}
          onChange={(e) => setNewGroup({...newGroup, name: e.target.value})}
          placeholder="Enter group name"
          style={{
            width: '100%',
            padding: '0.75rem',
            borderRadius: '0.375rem',
            border: '1px solid #e5e7eb',
            fontSize: '0.875rem',
            outline: 'none',
            transition: 'border-color 0.2s ease',
            backgroundColor: '#f9fafb'
          }}
        />
      </div>
      
      <div className="form-group" style={{ marginBottom: '1.5rem' }}>
        <label style={{ 
          display: 'block', 
          fontSize: '0.875rem',
          fontWeight: '500',
          color: '#111827',
          marginBottom: '0.5rem'
        }}>
          Description
        </label>
        <textarea
          value={newGroup.description}
          onChange={(e) => setNewGroup({...newGroup, description: e.target.value})}
          placeholder="Enter group description"
          style={{
            width: '100%',
            padding: '0.75rem',
            borderRadius: '0.375rem',
            border: '1px solid #e5e7eb',
            fontSize: '0.875rem',
            outline: 'none',
            resize: 'vertical',
            minHeight: '6rem',
            backgroundColor: '#f9fafb'
          }}
        />
      </div>
      
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '1rem'
      }}>
        <div className="form-group" style={{ marginBottom: '1.5rem' }}>
          <label style={{ 
            display: 'block', 
            fontSize: '0.875rem',
            fontWeight: '500',
            color: '#111827',
            marginBottom: '0.5rem'
          }}>
            Color
          </label>
          <div style={{ position: 'relative' }}>
            <button
              style={{
                width: '100%',
                height: '2.5rem',
                borderRadius: '0.375rem',
                border: '1px solid #e5e7eb',
                backgroundColor: newGroup.colorCode,
                cursor: 'pointer'
              }}
              onClick={() => setColorPickerOpen(!colorPickerOpen)}
            />
            
            {colorPickerOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  zIndex: 10,
                  marginTop: '0.25rem',
                  backgroundColor: '#ffffff',
                  borderRadius: '0.375rem',
                  border: '1px solid #e5e7eb',
                  padding: '0.5rem',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '0.25rem',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                }}
              >
                {[
                  '#e6376e', '#ef4444', '#f97316', '#f59e0b',
                  '#10b981', '#14b8a6', '#0ea5e9', '#3b82f6',
                  '#8b5cf6', '#a855f7', '#ec4899', '#f43f5e'
].map(color => (
  <button
    key={color}
    style={{
      width: '1.5rem',
      height: '1.5rem',
      borderRadius: '0.25rem',
      backgroundColor: color,
      border: color === newGroup.colorCode ? '2px solid #111827' : '1px solid #e5e7eb',
      cursor: 'pointer'
    }}
    onClick={() => {
      setNewGroup({...newGroup, colorCode: color});
      setColorPickerOpen(false);
    }}
  />
))}
</div>
)}
</div>
</div>

<div className="form-group" style={{ marginBottom: '1.5rem' }}>
<label style={{ 
display: 'block', 
fontSize: '0.875rem',
fontWeight: '500',
color: '#111827',
marginBottom: '0.5rem'
}}>
Member Limit
</label>
<input
type="number"
value={newGroup.membersLimit}
onChange={(e) => setNewGroup({...newGroup, membersLimit: parseInt(e.target.value, 10)})}
min={1}
max={50}
step={1}
style={{
width: '100%',
padding: '0.75rem',
borderRadius: '0.375rem',
border: '1px solid #e5e7eb',
fontSize: '0.875rem',
outline: 'none',
backgroundColor: '#f9fafb'
}}
/>
</div>
</div>
</div>

<div className="modal-footer" style={{
padding: '1.25rem',
borderTop: '1px solid #e5e7eb',
display: 'flex',
justifyContent: 'flex-end',
gap: '0.75rem',
position: 'sticky',
bottom: 0,
backgroundColor: '#ffffff',
zIndex: 1
}}>
<button
style={{
padding: '0.75rem 1rem',
backgroundColor: '#f3f4f6',
color: '#6b7280',
border: 'none',
borderRadius: '0.375rem',
fontSize: '0.875rem',
fontWeight: '500',
cursor: 'pointer',
transition: 'all 0.2s ease'
}}
onClick={() => setIsCreateModalOpen(false)}
>
Cancel
</button>
<button
style={{
display: 'flex',
alignItems: 'center',
gap: '0.5rem',
padding: '0.75rem 1rem',
backgroundColor: newGroup.name.trim() ? accentColor : '#d1d5db',
color: '#ffffff',
border: 'none',
borderRadius: '0.375rem',
fontSize: '0.875rem',
fontWeight: '500',
cursor: newGroup.name.trim() ? 'pointer' : 'not-allowed',
transition: 'all 0.2s ease'
}}
onClick={handleCreateGroup}
disabled={!newGroup.name.trim()}
>
<Save size={16} />
Create Group
</button>
</div>
</motion.div>
</div>
);
};

const renderDeleteModal = () => {
if (!isDeleteModalOpen || !selectedGroup) return null;

return (
<div className="modal-overlay" style={{
position: 'fixed',
top: 0,
left: 0,
right: 0,
bottom: 0,
backgroundColor: 'rgba(0, 0, 0, 0.5)',
display: 'flex',
justifyContent: 'center',
alignItems: 'center',
zIndex: 1000,
backdropFilter: 'blur(4px)'
}}>
<motion.div 
className="delete-modal"
initial={{ opacity: 0, scale: 0.9 }}
animate={{ opacity: 1, scale: 1 }}
transition={{ duration: 0.3 }}
style={{
backgroundColor: '#ffffff',
borderRadius: '0.5rem',
width: '100%',
maxWidth: '28rem',
boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
overflow: 'hidden'
}}
>
<div className="modal-header" style={{
padding: '1.25rem',
borderBottom: '1px solid #e5e7eb',
backgroundColor: '#f9fafb'
}}>
<div style={{ 
display: 'flex',
alignItems: 'center',
gap: '0.75rem'
}}>
<div style={{ 
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
width: '2.5rem',
height: '2.5rem',
borderRadius: '50%',
backgroundColor: '#fee2e2'
}}>
<AlertTriangle size={24} color="#ef4444" />
</div>
<h2 style={{ 
margin: 0, 
fontSize: '1.125rem', 
fontWeight: '600',
color: '#111827'
}}>
Delete Group
</h2>
</div>
</div>

<div className="modal-body" style={{ padding: '1.5rem' }}>
<p style={{ 
margin: '0 0 1.5rem 0', 
fontSize: '0.875rem',
color: '#6b7280',
lineHeight: '1.5'
}}>
Are you sure you want to delete the group "{selectedGroup.name}"? 
This action cannot be undone and will remove all associated data.
</p>

<div style={{ 
backgroundColor: '#f9fafb',
borderRadius: '0.375rem',
border: '1px solid #e5e7eb',
padding: '1rem',
marginBottom: '1.5rem'
}}>
<h3 style={{ 
margin: '0 0 0.5rem 0', 
fontSize: '0.875rem',
fontWeight: '600',
color: '#111827'
}}>
This will delete:
</h3>
<ul style={{ 
margin: 0,
padding: '0 0 0 1.25rem',
fontSize: '0.875rem',
color: '#6b7280'
}}>
<li style={{ marginBottom: '0.25rem' }}>All group data and settings</li>
<li style={{ marginBottom: '0.25rem' }}>All member associations</li>
<li style={{ marginBottom: '0.25rem' }}>All activity history</li>
</ul>
</div>

<div style={{ 
backgroundColor: '#fff5f5',
borderRadius: '0.375rem',
border: '1px solid #fee2e2',
padding: '0.75rem',
display: 'flex',
alignItems: 'center',
gap: '0.75rem'
}}>
<div style={{ 
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
width: '2rem',
height: '2rem',
borderRadius: '50%',
backgroundColor: '#fecaca',
flexShrink: 0
}}>
<AlertTriangle size={16} color="#ef4444" />
</div>
<div>
<p style={{ 
margin: 0, 
fontSize: '0.875rem',
color: '#b91c1c',
fontWeight: '500'
}}>
Warning: This action is permanent
</p>
<p style={{ 
margin: '0.25rem 0 0 0', 
fontSize: '0.75rem',
color: '#7f1d1d'
}}>
Group members will be notified of this change.
</p>
</div>
</div>
</div>

<div className="modal-footer" style={{
padding: '1.25rem',
borderTop: '1px solid #e5e7eb',
display: 'flex',
justifyContent: 'flex-end',
gap: '0.75rem',
backgroundColor: '#f9fafb'
}}>
<button
style={{
padding: '0.75rem 1rem',
backgroundColor: '#ffffff',
color: '#6b7280',
border: '1px solid #e5e7eb',
borderRadius: '0.375rem',
fontSize: '0.875rem',
fontWeight: '500',
cursor: 'pointer',
transition: 'all 0.2s ease'
}}
onClick={() => setIsDeleteModalOpen(false)}
>
Cancel
</button>
<button
style={{
display: 'flex',
alignItems: 'center',
gap: '0.5rem',
padding: '0.75rem 1rem',
backgroundColor: '#ef4444',
color: '#ffffff',
border: 'none',
borderRadius: '0.375rem',
fontSize: '0.875rem',
fontWeight: '500',
cursor: 'pointer',
transition: 'all 0.2s ease'
}}
onClick={handleDeleteGroup}
>
<Trash2 size={16} />
Delete Group
</button>
</div>
</motion.div>
</div>
);
};

// ========== RENDER NOTIFICATIONS & MESSAGES ==========
const renderNotifications = () => {
// Error message
const errorMessage = error && (
<motion.div 
className="error-message"
initial={{ opacity: 0, y: -20 }}
animate={{ opacity: 1, y: 0 }}
exit={{ opacity: 0, y: -20 }}
transition={{ duration: 0.3 }}
style={{
position: 'fixed',
top: '1.5rem',
right: '1.5rem',
backgroundColor: '#fee2e2',
borderRadius: '0.375rem',
boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
padding: '1rem',
zIndex: 1100,
maxWidth: '26rem',
display: 'flex',
alignItems: 'flex-start',
gap: '0.75rem',
border: '1px solid #fecaca'
}}
>
<div style={{ 
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
width: '1.5rem',
height: '1.5rem',
borderRadius: '50%',
backgroundColor: '#fecaca',
flexShrink: 0
}}>
<AlertTriangle size={14} color="#ef4444" />
</div>
<div style={{ flex: 1 }}>
<p style={{ 
margin: '0 0 0.5rem 0',
fontSize: '0.875rem',
fontWeight: '500',
color: '#b91c1c'
}}>
Error
</p>
<p style={{ 
margin: 0,
fontSize: '0.75rem',
color: '#991b1b'
}}>
{error}
</p>
</div>
<button
style={{
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
width: '1.5rem',
height: '1.5rem',
backgroundColor: 'transparent',
border: 'none',
cursor: 'pointer',
padding: 0,
color: '#991b1b'
}}
onClick={handleErrorClose}
>
<X size={14} />
</button>
</motion.div>
);

// Success message
const successMessage = success && (
<motion.div 
className="success-message"
initial={{ opacity: 0, y: -20 }}
animate={{ opacity: 1, y: 0 }}
exit={{ opacity: 0, y: -20 }}
transition={{ duration: 0.3 }}
style={{
position: 'fixed',
top: '1.5rem',
right: '1.5rem',
backgroundColor: '#d1fae5',
borderRadius: '0.375rem',
boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
padding: '1rem',
zIndex: 1100,
maxWidth: '26rem',
display: 'flex',
alignItems: 'flex-start',
gap: '0.75rem',
border: '1px solid #a7f3d0'
}}
>
<div style={{ 
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
width: '1.5rem',
height: '1.5rem',
borderRadius: '50%',
backgroundColor: '#a7f3d0',
flexShrink: 0
}}>
<Check size={14} color="#10b981" />
</div>
<div style={{ flex: 1 }}>
<p style={{ 
margin: '0 0 0.5rem 0',
fontSize: '0.875rem',
fontWeight: '500',
color: '#047857'
}}>
Success
</p>
<p style={{ 
margin: 0,
fontSize: '0.75rem',
color: '#065f46'
}}>
{success}
</p>
</div>
<button
style={{
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
width: '1.5rem',
height: '1.5rem',
backgroundColor: 'transparent',
border: 'none',
cursor: 'pointer',
padding: 0,
color: '#065f46'
}}
onClick={handleSuccessClose}
>
<X size={14} />
</button>
</motion.div>
);

return (
<AnimatePresence>
{errorMessage}
{successMessage}
</AnimatePresence>
);
};

// Loading indicator
const renderLoading = () => {
if (!isLoading) return null;

return (
<div style={{
position: 'fixed',
top: 0,
left: 0,
right: 0,
bottom: 0,
backgroundColor: 'rgba(255, 255, 255, 0.8)',
display: 'flex',
flexDirection: 'column',
justifyContent: 'center',
alignItems: 'center',
zIndex: 2000,
backdropFilter: 'blur(4px)'
}}>
<div style={{
width: '3rem',
height: '3rem',
borderRadius: '50%',
borderTop: `2px solid ${accentColor}`,
borderRight: '2px solid transparent',
animation: 'spin 1s linear infinite',
marginBottom: '1rem'
}} />
<div className="loading-bar" style={{
width: '12rem',
height: '0.25rem',
backgroundColor: '#e5e7eb',
borderRadius: '9999px',
overflow: 'hidden'
}}>
<div className="loading-bar-fill" style={{
height: '100%',
backgroundColor: accentColor,
width: '0%',
borderRadius: '9999px',
}} />
</div>
<p style={{ 
margin: '0.5rem 0 0 0',
fontSize: '0.875rem',
color: '#6b7280',
fontWeight: '500'
}}>
Processing...
</p>
</div>
);
};

// ========== MAIN RENDER ==========
return (
<div
style={{
height: '100%',
width: '100%',
position: 'relative',
backgroundColor: '#f5f7fb',
borderRadius: '0.5rem',
overflow: 'hidden',
"--accent-color": accentColor
}}
onDragOver={(e) => e.preventDefault()}
>
{/* Add keyframes for animations */}
<style>
{`
@keyframes pulse {
0% { transform: scale(1); }
50% { transform: scale(1.05); }
100% { transform: scale(1); }
}

@keyframes spin {
0% { transform: rotate(0deg); }
100% { transform: rotate(360deg); }
}

@keyframes shimmer {
0% { background-position: -1000px 0; }
100% { background-position: 1000px 0; }
}

@keyframes progress {
0% { width: 0%; }
100% { width: 100%; }
}

@keyframes slide-in {
0% { transform: translateY(20px); opacity: 0; }
100% { transform: translateY(0); opacity: 1; }
}

@keyframes fade-in {
0% { opacity: 0; }
100% { opacity: 1; }
}

.pulse {
animation: pulse 0.7s cubic-bezier(0.4, 0, 0.6, 1);
}

.spin {
animation: spin 1s linear;
}
`}
</style>

{/* Main structure */}
<div 
style={{
display: 'flex',
flexDirection: 'column',
height: '100%',
padding: '1.5rem',
overflowY: 'auto'
}}
>
<div style={{
display: 'flex',
justifyContent: 'space-between',
alignItems: 'center',
marginBottom: '1.5rem'
}}>
<div>
<h1 style={{ 
margin: '0 0 0.5rem 0', 
fontSize: '1.5rem', 
fontWeight: '700',
color: '#111827',
display: 'flex',
alignItems: 'center',
gap: '0.5rem'
}}>
<Users size={24} />
Group Management
</h1>
<p style={{ 
margin: 0, 
fontSize: '0.875rem',
color: '#6b7280'
}}>
Create, organize and manage groups for camp activities
</p>
</div>

<motion.button 
style={{
padding: '0.5rem',
borderRadius: '0.375rem',
border: 'none',
backgroundColor: '#f3f4f6',
color: '#6b7280',
cursor: 'pointer',
display: 'flex',
alignItems: 'center',
justifyContent: 'center'
}}
whileHover={{ scale: 1.1, backgroundColor: '#e5e7eb' }}
whileTap={{ scale: 0.95 }}
onClick={onClose}
>
<X size={20} />
</motion.button>
</div>

{/* Stats cards */}
{renderStatsCards()}

{/* Main content */}
<div style={{ flex: 1 }}>
{renderActionBar()}

<div style={{ 
display: 'grid', 
gridTemplateColumns: selectedGroup ? '1fr' : '1fr',
gap: '1.5rem' 
}}>
{renderGroupList()}
</div>

{selectedGroup && renderGroupDetails()}
</div>
</div>

{/* Modals */}
{renderCreateModal()}
{renderDeleteModal()}

{/* Messages */}
{renderNotifications()}

{/* Loading */}
{renderLoading()}
</div>
);
};

// Helper function to adjust color brightness
const adjustColor = (color, amount) => {
// Convert hex to RGB
let r = parseInt(color.substring(1, 3), 16);
let g = parseInt(color.substring(3, 5), 16);
let b = parseInt(color.substring(5, 7), 16);

// Adjust
r = Math.max(0, Math.min(255, r + amount));
g = Math.max(0, Math.min(255, g + amount));
b = Math.max(0, Math.min(255, b + amount));

// Convert back to hex
return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

export default GroupManagementScreen;